import { BrandProfile, ContentIdea } from "../types";

export enum Type {
  STRING = "STRING",
  NUMBER = "NUMBER",
  INTEGER = "INTEGER",
  BOOLEAN = "BOOLEAN",
  OBJECT = "OBJECT",
  ARRAY = "ARRAY",
}
import { compressBase64Image } from "./utils";
import { logError } from "./logger";
import { auth, db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

const GLOBAL_OBSERVANCES: Record<string, { date: string; name: string }[]> = {
  "January": [
    { date: "01", name: "New Year's Day" },
    { date: "15", name: "World Religion Day" },
    { date: "24", name: "International Day of Education" }
  ],
  "February": [
    { date: "04", name: "World Cancer Day" },
    { date: "11", name: "International Day of Women and Girls in Science" },
    { date: "14", name: "Valentine's Day" },
    { date: "20", name: "World Day of Social Justice" }
  ],
  "March": [
    { date: "08", name: "International Women's Day" },
    { date: "20", name: "International Day of Happiness" },
    { date: "21", name: "International Day for the Elimination of Racial Discrimination" },
    { date: "22", name: "World Water Day" }
  ],
  "April": [
    { date: "07", name: "World Health Day" },
    { date: "22", name: "Earth Day" },
    { date: "23", name: "World Book Day" }
  ],
  "May": [
    { date: "01", name: "International Workers' Day" },
    { date: "15", name: "International Day of Families" },
    { date: "21", name: "World Day for Cultural Diversity" }
  ],
  "June": [
    { date: "05", name: "World Environment Day" },
    { date: "08", name: "World Oceans Day" },
    { date: "20", name: "World Refugee Day" },
    { date: "21", name: "International Day of Yoga" }
  ],
  "July": [
    { date: "18", name: "Nelson Mandela International Day" },
    { date: "30", name: "International Day of Friendship" }
  ],
  "August": [
    { date: "12", name: "International Youth Day" },
    { date: "19", name: "World Humanitarian Day" }
  ],
  "September": [
    { date: "08", name: "International Literacy Day" },
    { date: "21", name: "International Day of Peace" },
    { date: "27", name: "World Tourism Day" }
  ],
  "October": [
    { date: "01", name: "International Day of Older Persons" },
    { date: "10", name: "World Mental Health Day" },
    { date: "16", name: "World Food Day" },
    { date: "24", name: "United Nations Day" },
    { date: "31", name: "Halloween / World Cities Day" }
  ],
  "November": [
    { date: "16", name: "International Day for Tolerance" },
    { date: "20", name: "World Children's Day" },
    { date: "25", name: "International Day for the Elimination of Violence against Women" }
  ],
  "December": [
    { date: "01", name: "World AIDS Day" },
    { date: "10", name: "Human Rights Day" },
    { date: "25", name: "Festive Season / Global Observance" },
    { date: "31", name: "New Year's Eve" }
  ]
};

let secureConfig: any = null;
export const setSecureConfig = (config: any) => {
  secureConfig = config;
};

export const getApiKey = (type: 'standard' | 'premium' = 'standard') => {
  const env = (window as any)._env_ || {};
  const safeProcessEnv = (typeof process !== 'undefined' && process.env) || {};
  
  let rawKey: any;
  if (type === 'premium') {
    rawKey = env.PLATAPOST_AI_KEY || env.PAID_GEMINI_API_KEY || safeProcessEnv.PLATAPOST_AI_KEY || safeProcessEnv.PAID_GEMINI_API_KEY || env.GEMINI_API_KEY || safeProcessEnv.GEMINI_API_KEY;
  } else {
    rawKey = env.PLATAPOST_AI_KEY || safeProcessEnv.PLATAPOST_AI_KEY || env.GEMINI_API_KEY || safeProcessEnv.GEMINI_API_KEY || env.PAID_GEMINI_API_KEY || safeProcessEnv.PAID_GEMINI_API_KEY;
  }
  
  // Try to use environment variable
  if (rawKey && typeof rawKey === 'string') {
    const key = rawKey.trim();
    if (key && key !== 'undefined' && key !== 'null') {
      return key;
    }
  }

  // Fallback to secure config
  if (secureConfig) {
    let key = type === 'premium' ? secureConfig.PAID_GEMINI_API_KEY : secureConfig.GEMINI_API_KEY;
    if (!key && type === 'standard') key = secureConfig.PAID_GEMINI_API_KEY;
    if (!key && type === 'premium') key = secureConfig.GEMINI_API_KEY;
    if (key) return key.trim();
  }
  
  return undefined;
};

export function fixCaptionSpacing(text: string): string {
  if (!text || typeof text !== 'string') return text;
  
  // Fix period preceded by a letter and followed immediately by an uppercase letter.
  // E.g., "advantage.July" -> "advantage. July", "guidelines.We" -> "guidelines. We"
  let cleaned = text.replace(/(?<=[a-zA-Z])\.(?=[A-Z])/g, ". ");

  // Fix exclamation or question mark preceded by a letter and followed immediately by any letter.
  // E.g., "now!we" -> "now! we", "now?we" -> "now? we"
  cleaned = cleaned.replace(/(?<=[a-zA-Z])([!?])(?=[a-zA-Z])/g, "$1 ");

  // Fix comma preceded by a letter and followed immediately by any letter.
  // E.g., "July,we" -> "July, we"
  cleaned = cleaned.replace(/(?<=[a-zA-Z]),(?=[a-zA-Z])/g, ", ");

  return cleaned;
}

// --- PROXY HELPER ---
async function callGeminiProxy(params: {
  model: string;
  contents?: any[];
  config?: any;
  type?: 'standard' | 'premium';
  action?: 'generateContent' | 'generateImages' | 'generateVideos' | 'getOperation';
  prompt?: string;
  operationName?: string;
}) {
  const user = auth.currentUser;
  if (!user) throw new Error("Authentication required for AI generation.");
  
  const token = await user.getIdToken();
  const response = await fetch('/api/gemini/proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  let data: any;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    const rawText = await response.text();
    const cleanError = rawText.length > 200 ? rawText.substring(0, 200) + "..." : rawText;
    throw new Error(`Server returned non-JSON error (Status ${response.status}): ${cleanError}`);
  }

  if (!response.ok) {
    // Check for specific error types to relay to UI (server proxy provides userMessage for common issues)
    const errorMsg = data.userMessage || data.details || data.error?.message || data.error || "AI generation failed";
    throw new Error(errorMsg);
  }

  // Mimic SDK response structure for generateContent
  if (!params.action || params.action === 'generateContent') {
    return {
      ...data,
      text: data.candidates?.[0]?.content?.parts?.map((p: any) => p.text).join('') || ""
    };
  }
  
  return data;
}
// --------------------

export async function analyzeSocialSentiment(text: string): Promise<{
  sentiment: "positive" | "neutral" | "negative";
  urgency: "low" | "medium" | "high";
  intent: "question" | "complaint" | "praise" | "lead" | "spam";
  topic: "pricing" | "service" | "delivery" | "product quality" | "support";
  suggestedReply: string;
  confidence: number;
}> {
  const prompt = `
    Analyze the following social media comment or message and provide structured sentiment metadata.
    
    TEXT: "${text}"

    Return JSON with:
    - sentiment: "positive" | "neutral" | "negative"
    - urgency: "low" | "medium" | "high" (complaints are high)
    - intent: "question" | "complaint" | "praise" | "lead" | "spam"
    - topic: "pricing" | "service" | "delivery" | "product quality" | "support"
    - suggestedReply: A professional, helpful response.
    - confidence: 0.0 to 1.0

    Return ONLY the JSON.
  `;

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{ parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json"
      }
    });
    const content = response.text || "{}";
    const clean = content.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch (err) {
    console.error("Sentiment analysis failed:", err);
    return {
      sentiment: "neutral",
      urgency: "low",
      intent: "question",
      topic: "support",
      suggestedReply: "Thanks for your message!",
      confidence: 0.5
    };
  }
}

async function getActiveBlueprintsPrompt(brandName?: string): Promise<string> {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return '';
    const emailLower = currentUser.email?.toLowerCase() || '';
    const isMe = emailLower === 'dewaldjordaan5@gmail.com' || emailLower === 'dewald.jordan@sparkpr.com';
    
    // STRICT RULE: Only test blueprint templating on Volterra Dental
    const isVolterra = brandName && brandName.toLowerCase().includes('volterra');
    if (!isMe || !isVolterra) return '';

    const q = query(collection(db, 'carousel_blueprints'), where('active', '==', true));
    const snap = await getDocs(q);
    const blueprints: any[] = [];
    snap.forEach(doc => {
      blueprints.push(doc.data());
    });
    
    if (blueprints.length === 0) return '';
    
    let promptChunk = `\n\nKNOWLEDGE BASE OF CAROUSEL LAYOUT BLUEPRINTS:
You have access to a custom, professional knowledge base of carousel layout blueprints. When generating a carousel post, you may (and are highly encouraged to) choose one of these blueprints to structure the narrative flow, slide count, and visual concepts of the slides:
`;
    blueprints.forEach((b: any, index: number) => {
      promptChunk += `\n--- Blueprint ${index + 1}: ${b.name} (${b.category}) ---
Description: ${b.description}
Recommended Slide Count: ${b.slideCount}
Detailed Slide-by-Slide Narrative Guidelines:
${b.narrative}
`;
      if (b.layoutTemplate?.boxes) {
        promptChunk += `
CRITICAL LAYOUT RULES FOR THIS BLUEPRINT:
If you choose this blueprint, the template has specific text placeholders that MUST be filled.
To feed these placeholders perfectly, you MUST write the slide's "overlayText" as a structured block using explicit, ALL-CAPS tags to identify which part of the text belongs to which box!
Always format the "overlayText" of your slides using this tag structure whenever writing template-specific content:
[EYEBROW] <your eyebrow text, if this blueprint has an eyebrow box>
[HEADING] <your heading text, if this blueprint has a heading box>
[BODY] <your body text or description, if this blueprint has a body/paragraph box>
[BULLETS]
- <item 1, if this blueprint has a list or bullets box>
- <item 2>
[CTA] <call-to-action button text, if this blueprint has a cta box>

Do not worry about strict hard truncation in length—we have a premium responsive Auto-Fit text engine in the UI that dynamically decreases or increases font sizes to perfectly fit the boxes without overflow! But strive for maximum impact, readability, and design balance.
Here are the boxes defined for this template, and their targeted optimal readability limit:
${b.layoutTemplate.boxes.filter((box: any) => box.type !== 'logo').map((box: any) => {
  const area = (box.width || 50) * (box.height || 10);
  let charLimit = 50;
  if (box.type === 'heading') charLimit = Math.max(30, Math.round(area * 0.15));
  else if (box.type === 'body' || box.type === 'icon-list') charLimit = Math.max(80, Math.round(area * 0.45));
  else if (box.type === 'eyebrow' || box.type === 'cta') charLimit = Math.max(25, Math.round(area * 0.1));
  return `- Box ID "${box.id}" (Type: "${box.type}", size: ${box.width}% width by ${box.height}% height). Target readability under ~${charLimit} chars.`;
}).join("\n")}

IMPORTANT WRITING RULES:
1. DO NOT use, place, or suggest ⚡ (lightning bolt) or any other inline emojis in the text overlays. Focus on world-class professional authority.
2. For lists/bullets, do not write generic introductory text. List your highly actionable key items directly.
`;
      }
    });
    
    promptChunk += `\n\nINSTRUCTION: When generating a carousel post, if one of the above blueprints fits the post's objective beautifully, structure the post's slides exactly following that blueprint's narrative guidelines and target slide count. If you apply a blueprint, match the visual concepts of the slides to the detailed slide narrative guidelines provided in the blueprint.`;
    return promptChunk;
  } catch (err) {
    console.error("Failed to fetch active templates from blueprint engine:", err);
    return '';
  }
}

export async function generateMonthlyCalendar(
  brand: BrandProfile, 
  highlights: string[], 
  products: string, 
  goals: string, 
  month: string, 
  year: number,
  isTrial: boolean = false,
  performanceInsights?: string,
  numberOfPosts: number | string = 30,
  targetChannel: string = 'default',
  customInstructions: string = '',
  latestTrends?: string
): Promise<ContentIdea[]> {
  const docParts: any[] = [];
  if (brand.contextDocuments && brand.contextDocuments.length > 0) {
    // Limit to first 2 documents to save tokens and prevent quota hits
    const limitedDocs = brand.contextDocuments.slice(0, 2);
    for (const doc of limitedDocs) {
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        docParts.push({
          inlineData: {
            data: base64,
            mimeType: doc.mimeType
          }
        });
      } catch (err) {
        console.error("Failed to load context document:", doc.name, err);
      }
    }
  }

  const profileContext = brand.accountType === 'personal'
    ? `This is a personal brand/creator profile. Generate content presenting them as an individual thought leader, using first-person language ("I", "me", "my") unless another perspective is specified.`
    : `This is a company/business brand. Generate content representing the company, using standard brand voices ("we", "our", "us").`;

  let prompt = `
    Generate a social media content calendar for ${month} ${year} for the following brand:
    Brand Name: ${brand.companyName}
    Profile Type/Context: ${profileContext}
    Location/Target Market: ${brand.location || 'Not specified'}
    Website: ${brand.website || 'N/A'}
    Branding Details: ${brand.brandingDetails}
    Target Audience: ${brand.questionnaire.targetAudience}
    Brand Voice: ${brand.questionnaire.brandVoice}
    
    ${brand.strategicLens ? `STRATEGIC LENS (User Specific Focus): ${brand.strategicLens}\nINSTRUCTION: Analyze all content ideas through this lens. Ensure the content addresses or leverages the specific themes mentioned here.` : ''}

    ${latestTrends ? `
    LATEST TRENDS & MARKET INTELLIGENCE:
    ${latestTrends}
    
    INSTRUCTION: This is high-priority intelligence. Incorporate these trends into at least 30-50% of the generated posts where relevant. Specifically, look at how these external trends interact with the brand's niche and strategic lens.` : ''}

    ${docParts.length > 0 ? 'I have attached several context documents (PDFs/Text) containing company information, pricing, and strategy. Please analyze these documents and use the information within them to make highly informed choices for the content calendar.' : ''}

    ${performanceInsights ? `
    SOCIAL MEDIA PERFORMANCE INSIGHTS (Last 30 Days):
    ${performanceInsights}
    
    INSTRUCTION: Use these insights to optimize the new calendar. Focus more on themes, tones, and formats that performed well (high engagement/reach) and shift away from content types that didn't resonate.` : ''}

    Plan Specifics:
    Key Products/Services: ${products}
    Specific Goals: ${goals}
    Monthly Highlights/Projects:
    ${highlights.map((h, i) => `${i + 1}. ${h}`).join('\n')}
    
    AVAILABLE BRAND ASSETS:
    - Products/Services: ${brand.productImages?.map(p => `${p.name}: ${p.description || 'N/A'} (Price: ${p.price || 'N/A'})`).join(', ') || 'None'}
    - Team Directory/Members: ${brand.teamDirectory?.map(t => `${t.name} (${t.role}): ${t.bio || 'N/A'}`).join(', ') || 'None'}
    - Visual Templates: ${brand.brandTemplates?.map(t => `${t.name}: ${t.description || 'N/A'}`).join(', ') || 'None'}
    - Approved Stock/Camera Media Gallery: ${brand.approvedMedia?.map(m => `"${m.name}": ${m.description || 'N/A'}`).join(', ') || 'None'}
    
    Available Platforms: ${targetChannel && targetChannel !== 'default' ? targetChannel : brand.platforms.join(', ')}
    
    Standard Observances for ${month}:
    ${(GLOBAL_OBSERVANCES[month] || []).map(o => `- ${month} ${o.date}: ${o.name}`).join('\n')}

    Requirements:
    - ${isTrial ? 'Generate EXACTLY 1 high-quality content idea to serve as a sample. Do not generate more than 1.' : (numberOfPosts === 'upload' ? 'Parse the provided user plan in the custom instructions, review it against the brand profile, reformat it into the exact JSON structure required, and generate the corresponding posts. Retain all posts requested by the user, enhancing the copy and creative direction.' : `Generate EXACTLY ${Math.min(30, typeof numberOfPosts === 'number' ? numberOfPosts : 30)} strategic content ideas. Target this number closely, do not generate significantly more.`)}
    ${customInstructions ? `- CRITICAL USER INSTRUCTIONS FOR THIS CALENDAR:\n      ${customInstructions}` : ''}
    - APPROVED MEDIA PHOTO INTEGRATION: If there are approved stock/camera photos listed under 'Approved Stock/Camera Media Gallery', and any photo's name/description relates to the topic of a post (e.g., clinic staff members, reception, specific dental setup etc.), you are encouraged to use it. When you decide to reference an approved photo, write '[USE APPROVED MEDIA: Exact Name of Photo]' at the very beginning of the post or slide 'concept' and 'visualConcept' so the generation system automatically loads that photo instead of generating a generic one. Do not force it; only use it when it perfectly fits the context of the post.
    - STRATEGY: You MUST generate separate, dedicated post objects for each target platform so they can have platform-specific wording, hashtags, and lengths. NEVER combine multiple platforms (e.g., ["Facebook", "Instagram"]) into a single post object.
    - MULTI-POST DAYS: You are encouraged to schedule multiple distinct posts on the same day if they target different platforms or serve different sub-goals.
    - WORLD-CLASS COPYWRITING RULES:
      1. Human Tension over Ad-speak: Never write generic, corporate, or promotional ad-speak (avoid phrases like "are you tired of...", "look no further!", "introducing...", "we're excited to announce", "supercharge your...", "unlock the secret...", "solutions to help you"). Live at the intersection of a Human Tension and a specific Truth.
      2. Perfect Hook Structure: The first line (caption hook) must stop the scroll. It should be a contrarian opinion, a surprising statistic, a high-value question, an outcome-first claim, or a highly relatable problem. Never use generic labels as hooks (e.g., do not start with "Here's why you should...").
      3. Narrative Architecture: Group the caption body into clear narrative chunks:
         - Hook (Scroll-stopper)
         - Context/Story/Tension (Why this matters right now)
         - Practical Value/Core Idea (Actionable bullet points or insights the reader can execute immediately)
         - CTA (A highly direct, low-friction next step like "Comment WORK below and I'll send you...", "Save this for your next planning session", or "DM us to get started").
      4. Formatting and Spacing: ALWAYS ensure there is exactly one space after every period, comma, colon, semicolon, exclamation mark, or question mark when followed by a new word. NEVER run words together across punctuation (do NOT write things like "advantage.July" or "guidelines.We"; write "advantage. July" and "guidelines. We"). Use frequent paragraph breaks, very few or zero generic emojis (never use more than 1-2 highly context-relevant emojis total). Never pile an emoji soup. Write beautifully, using short, punchy sentences. Make it sound like a top 1% industry expert writing.
      5. COPYWRITING TONE (HARD RULE): Do NOT use cheesy puns (e.g., "trick-or-treat yourself", "Halloween candy rush", "spook-tacular"). Your copy must be sophisticated, punchy, elegant, and world-class. Write like a high-end luxury or top-tier tech agency copywriter. Be authoritative and valuable.
    - COPY RULE: The 'copy' field is for a SINGLE caption. NEVER write multiple versions of a caption (e.g., one for Facebook, one for Instagram) in the same copy text box. Since you are strictly generating one post per platform, write the copy natively for that specific platform (e.g., short and punchy for Instagram with emojis, professional for LinkedIn).
    - Every single post MUST directly serve the brand's specific goals and monthly highlights while maintaining the designated tone and voice.
    - Ensure the posts follow a logical narrative or theme throughout the month to maintain a consistent feed.
    - Vary the content types (educational, promotional, lifestyle, behind-the-scenes) to keep the audience engaged.
    - Each idea should specify which platforms it's for (from the available list). 
    - IMPORTANT: For the standard observances listed above, generate friendly, inclusive, and non-intrusive content. Use terms like "Festive Season" or "Season of Giving" instead of specific religious names unless the brand context explicitly suggests otherwise. The messaging should be warm and community-focused.
    - Not every post should be for every platform. Some days should have posts for all platforms, some days only one or two. Choose platforms based on "platform fit" for the message and "best times to post" for that specific platform.
    - Include a mix of formats: 'feed', 'story', and 'carousel'.
    - SLIDE COUNT RULES:
      1. If format is 'carousel', dynamically determine the optimal number of slides (between 2 and 10).
      2. If format is 'feed' or 'story', you MUST generate EXACTLY 1 slide. No more, no less.
    - CAROUSEL POST QUALITY RULES & COHESION (If format is 'carousel'):
      1. Every Slide Must Earn the Next Swipe: Each slide must have one clear purpose, one main idea, and enough curiosity or utility to make the reader want to continue. Do not treat the carousel as an information dump.
      2. Strict Narrative Layout Flow:
         - Slide 1: Hook - Start with a bold, specific, scroll-stopping headline that creates curiosity, challenges a belief, highlights a major pain point, or promises an extremely useful outcome.
         - Slide 2: Problem or Context - Explain why the topic matters. Show the pain point, opportunity, mistake, or common misconception.
         - Middle slides: Value Delivery - Teach the core concepts using short, clear, highly practical points. Each individual slide must focus on exactly one single idea.
         - Example or Application slide - Always include an actionable example, "Do This, Not That" comparison, template, or before/after demonstration. Never give purely abstract advice.
         - Summary slide - Recap the key takeaways in a simple, memorable, saveable checklist format.
         - Final slide: CTA - End with a clear, single call to action that aligns with the post's objective (e.g., Save, Comment, DM, Share, or Visit Link).
      3. Mobile-First Readability & Style:
         - Keep text short and easy to scan. Avoid large blocks of paragraph text. Use bold headlines paired with clean supporting bullet points or short descriptions.
         - Every slide must feel like a single focused thought that can be fully comprehended in 2-3 seconds on a small screen.
      4. Infographics, Diagrams & Structural Breakdowns:
         - Use infographics, timelines, funnels, cause-and-effect flowcharts, step-by-step layouts, or anatomical breakdowns only when they make the idea easier to understand, not just for decoration.
         - COMPREHENSIVE IMAGE CONCEPT MANDATE (HARD RULE): If a slide contains a diagram, blueprint, chart, or infographic, the visual 'concept' field MUST describe it in extreme, meticulous detail, listing every single indicator arrow, part, label, connector, and layout. (e.g., "A modern infographic illustration showing a clean flow with uppercase thin indicators pointing to precise text tags: 'STEP 1: PREPARATION', 'STEP 2: ACTIVATION', and 'STEP 3: ENHANCEMENT'. The layout is clean and centered in the bottom 60% with a highly precise presentation style"). This gives the artwork engine enough details to produce clean, professional visual elements without empty spaces.
      5. PHOTOGRAPHY & CREATIVE DIRECTION (HARD RULE):
         - DO NOT write hyper-literal, cheesy, or uncanny image concepts (e.g., do NOT ask for "a pumpkin with human teeth", "a smiling tooth holding a toothbrush", or "a doctor giving a thumbs up").
         - Instead, use premium, high-end lifestyle photography, aesthetic architectural shots, abstract moody geometry, or highly polished cinematic scenes. Keep the visual subject dignified and elegant.
         - IMPORTANT: Ensure the background image or post artwork aligns perfectly with the message of the copy. Do not just use a generic template background—create an image concept that conceptually works with the specific slide copy.
      6. Consistent Background & Panorama Continuity:
         - Treat the slides as one horizontal panorama. Describe continuous background threads, lines, or scenic elements connecting them seamlessly from left to right.
          - Align visual subjects and text blocks to the exact same relative location across slides (e.g., centered bottom 60% for subjects, top 40% for headings) to prevent chaotic jumping. Re-use the same borders, icons, and color patterns.
      7. Text Card / Overlay Text Rule (HARD RULE): If any slide concept mentions or implies rendering text, you MUST provide the exact string of text to overlay in the "overlayText" field. ${brand.companyName?.toLowerCase().includes('volterra') ? `Formats must be structured with the following ALL-CAPS tags indicating where lines belong. ONLY USE THE TAGS IF RELEVANT (e.g. do not include [BULLETS] if there is no list). DO NOT invent random tags.
         CRITICAL TEXT LENGTH LIMITS:
         - [HEADING] must be between 5 - 12 words or max 65 characters (no one or two word headings).
         - [BODY] must be max 120 characters if it has bullet points. If there are NO bullet points, the text can expand downwards up to max 220 characters.
         - [BULLETS] Optional. ONLY INCLUDE IF THE SLIDE CONCEPT ACTUALLY CALLS FOR A LIST (like a checklist or key points). If there are NO bullet points needed, do NOT include the [BULLETS] tag or list items at all.
         [EYEBROW] <your eyebrow text>
         [HEADING] <your heading text>
         [BODY] <your concise body text>
         [BULLETS]
         - <short item 1>
         - <short item 2>
         [CTA] <button text>

         Emojis and lightning bolts (⚡) are STRICTLY FORBIDDEN on the slide artwork text overlays. Do not worry about strict limits because our layout blocks scale font sizes up or down dynamically using an Auto-Fit system to always look pristine.` : `Ensure the text is concise and fits well visually.`}
    - VISUAL TEMPLATE MATCHING (HARD RULE): If the brand provides "Visual Templates" in the assets (e.g. "Testimonial Template", "Sale Flyer"), and a slide's purpose matches that template, you MUST explicitly include the exact template name inside brackets like "[TEMPLATE: Exact Template Name]" in the slide's visual 'concept' field. This will tell our rendering engine to use their uploaded template as inspiration.
    - Provide engaging copy and a visual concept for each slide.
    - Spread them across the month.
    - For each post, provide a "bestTimeToPost" field (e.g., "Tuesday at 10:00 AM") based on industry best practices for the selected platforms.
    - IMPORTANT: Ensure each post has a unique, randomly generated string for the "id" field (e.g., "post_abc123"). Do not use simple numbers like "1", "2", "3".
    ${brand.website ? '- Use the provided website to inform the brand colors, tone, and style.' : ''}
  `;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          date: { type: Type.STRING, description: "ISO date string" },
          platforms: { 
            type: Type.ARRAY, 
            items: { 
              type: Type.STRING,
              enum: ["Facebook", "Instagram", "LinkedIn", "Twitter", "Reddit", "Pinterest", "TikTok"]
            } 
          },
          copy: { type: Type.STRING },
          concept: { type: Type.STRING, description: "Overall visual concept" },
          format: { type: Type.STRING, enum: ["feed", "story", "carousel"] },
          type: { type: Type.STRING, enum: ["evergreen", "highlight", "promo", "event"] },
          bestTimeToPost: { type: Type.STRING, description: "Guidance on the best time of day to post this content" },
          slides: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                concept: { type: Type.STRING, description: "Specific visual concept for this slide. MUST follow the narrative flow rules (Slide 1 must be a bold scroll-stopping Hook; Slide 2 must show Problem/Context; Middle slides must teach 1 specific Value idea with continuous backdrop; Example-slide shows 'Do This, Not That'/template; Summary slide shows a saveable checklist; Final slide shows a clear CTA). For diagrams or infographics, describe exact anatomical parts, arrows, and labels in meticulous detail." },
                imagePrompt: { type: Type.STRING, description: "A pure visual prompt for the background image generator. STRICTLY NO text, NO plaques, NO bento grids, and NO ui mockups. ONLY describe the scenic photography, colors, or objects (e.g. 'A bright dental waiting room with natural lighting')." },
                overlayText: { type: Type.STRING, description: "The exact headline, checklist points, list content, or quote text to be overlaid on this slide. MUST NOT be empty or blank if the slide concept implies or contains text, headlines, lists, bullet points, summaries, or quotes." }
              },
              required: ["id", "concept"]
            }
          }
        },
        required: ["id", "date", "platforms", "copy", "concept", "format", "type", "slides", "bestTimeToPost"]
      }
    }
  };

  if (brand.website) {
    // config.tools = [{ googleSearch: {} }]; // Not supported with responseMimeType: 'application/json'
    prompt += `\nInclude context from their website if possible, based on your general knowledge.`;
  }

  const blueprintPrompt = await getActiveBlueprintsPrompt(brand.companyName);
  if (blueprintPrompt) {
    prompt += blueprintPrompt;
  }

  let responseText = "";
  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [...docParts.map(p => ({ parts: [{ inlineData: p.inlineData }] })), { parts: [{ text: prompt }] }],
      config: config
    });
    responseText = response.text || "[]";
  } catch (err: any) {
    if (err.message && (err.message.includes("403") || err.message.includes("Forbidden")) && config.tools) {
      console.warn("Google Search grounding failed with 403 Forbidden. Retrying without search...", err);
      delete config.tools;
      const retryResponse = await callGeminiProxy({
        model: "gemini-2.5-flash",
        contents: [...docParts.map(p => ({ parts: [{ inlineData: p.inlineData }] })), { parts: [{ text: prompt }] }],
        config: config
      });
      responseText = retryResponse.text || "[]";
    } else {
      await logError("Plan generation failed", { month, year, brand: brand.companyName, error: err.message }, err);
      throw err;
    }
  }

  try {
    const parsed = JSON.parse(responseText);
    if (Array.isArray(parsed)) {
      return parsed.map((item: any) => {
        const postId = 'post_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
        return {
          ...item,
          id: postId,
          copy: fixCaptionSpacing(item.copy || ""),
          concept: fixCaptionSpacing(item.concept || ""),
          slides: (item.slides || []).map((s: any) => ({
            ...s,
            id: 'slide_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36),
            concept: fixCaptionSpacing(s.concept || ""),
            overlayText: s.overlayText ? fixCaptionSpacing(s.overlayText) : s.overlayText
          }))
        };
      });
    }
    return parsed;
  } catch (err: any) {
    await logError("Failed to parse plan JSON", { response: responseText, error: err.message }, err);
    throw new Error("Failed to parse the generated plan. Please try again.");
  }
}

export async function generateSinglePost(
  brand: BrandProfile,
  postDescription: string,
  date: string,
  preferredFormat?: 'feed' | 'story' | 'carousel',
  isPriority: boolean = false,
  visualDirection?: string,
  visualBlueprint?: any
): Promise<ContentIdea> {
  const docParts: any[] = [];
  if (brand.contextDocuments && brand.contextDocuments.length > 0) {
    // Limit to first 2 documents to save tokens and prevent quota hits
    const limitedDocs = brand.contextDocuments.slice(0, 2);
    for (const doc of limitedDocs) {
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        docParts.push({
          inlineData: {
            data: base64,
            mimeType: doc.mimeType
          }
        });
      } catch (err) {
        console.error("Failed to load context document:", doc.name, err);
      }
    }
  }

  const profileContext = brand.accountType === 'personal'
    ? `This is a personal brand/creator profile. Generate content presenting them as an individual thought leader, using first-person language ("I", "me", "my").`
    : `This is a company/business brand. Generate content representing the company, using standard brand voices ("we", "our", "us").`;

  const prompt = `
    Generate a single social media post based on the following description:
    Description: ${postDescription}
    Date: ${date}
    ${preferredFormat ? `PREFERRED FORMAT: ${preferredFormat}` : ''}
    ${visualDirection ? `VISUAL DIRECTION / STYLE: ${visualDirection} (e.g. text card, quote card, before/after, photography. Use this style when generating the visual concept for the slides.)` : ''}
    
    ${visualBlueprint?.layoutTemplate?.boxes ? `
    CRITICAL LAYOUT RULES FOR CHOSEN TEMPLATE:
    You are writing content to fit a precise blueprint graphic template. 
    The template has specific text placeholders that MUST be filled appropriately:
    ${visualBlueprint.layoutTemplate.boxes.filter((b: any) => b.type !== 'logo').map((b: any) => 
      `- ${b.id} (${b.type}): Original length was ~${Math.max(2, (b.text || '').split(" ").length)} words. Suggest a natural, impactful replacement of SIMILAR OR SHORTER length to avoid layout overflow.`
    ).join("\n")}
    
    1. For headings and eyebrow text, write punchy, short copy to prevent text from wrapping incorrectly. Never leave a single word to wrap onto a new line (orphan).
    2. For fields of type 'icon-list', you MUST separate the list items exclusively with the newline character (\\n). If the post content does NOT require a bulleted list, you MUST leave the 'icon-list' field completely empty (an empty string ""). IF there are exactly N items in the original template's list, strongly prefer providing N items.
    3. The content generated in the "overlayText" MUST map cleanly to these blocks.
    ` : ''}

    ${isPriority ? 'CRITICAL INSTRUCTION: This is a high-priority, specific concept. You MUST prioritize the details and "Hook" provided in the description. Do NOT dilute it with general filler. Stay extremely close to the specific value proposition mentioned.' : ''}

    Brand Context:
    Brand Name: ${brand.companyName}
    Profile Type/Context: ${profileContext}
    Branding Details: ${brand.brandingDetails}
    Target Audience: ${brand.questionnaire.targetAudience}
    Brand Voice: ${brand.questionnaire.brandVoice}
    Available Platforms: ${brand.platforms.join(', ')}

    AVAILABLE BRAND ASSETS:
    - Products/Services: ${brand.productImages?.map(p => `${p.name}: ${p.description || 'N/A'} (Price: ${p.price || 'N/A'})`).join(', ') || 'None'}
    - Team Directory/Members: ${brand.teamDirectory?.map(t => `${t.name} (${t.role}): ${t.bio || 'N/A'}`).join(', ') || 'None'}
    - Visual Templates: ${brand.brandTemplates?.map(t => `${t.name}: ${t.description || 'N/A'}`).join(', ') || 'None'}

    Requirements:
    - Rewrite the description into a professional, engaging social media post.
    - WORLD-CLASS COPYWRITING RULES:
      1. Human Tension over Ad-speak: Never write generic, corporate, or promotional ad-speak (avoid phrases like "are you tired of...", "look no further!", "introducing...", "we're excited to announce", "supercharge your...", "unlock the secret...", "solutions to help you"). Live at the intersection of a Human Tension and a specific Truth.
      2. Perfect Hook Structure: The first line (caption hook) must stop the scroll. It should be a contrarian opinion, a surprising statistic, a high-value question, an outcome-first claim, or a highly relatable problem. Never use generic labels as hooks (e.g., do not start with "Here's why you should...").
      3. Narrative Architecture: Group the caption body into clear narrative chunks:
         - Hook (Scroll-stopper)
         - Context/Story/Tension (Why this matters right now)
         - Practical Value/Core Idea (Actionable bullet points or insights the reader can execute immediately)
         - CTA (A highly direct, low-friction next step like "Comment WORK below and I'll send you...", "Save this for your next planning session", or "DM us to get started").
      4. Formatting and Spacing: ALWAYS ensure there is exactly one space after every period, comma, colon, semicolon, exclamation mark, or question mark when followed by a new word. NEVER run words together across punctuation (do NOT write things like "advantage.July" or "guidelines.We"; write "advantage. July" and "guidelines. We"). Use frequent paragraph breaks, very few or zero generic emojis (never use more than 1-2 highly context-relevant emojis total). Never pile an emoji soup. Write beautifully, using short, punchy sentences. Make it sound like a top 1% industry expert writing.
      5. COPYWRITING TONE (HARD RULE): Do NOT use cheesy puns (e.g., "trick-or-treat yourself", "Halloween candy rush", "spook-tacular"). Your copy must be sophisticated, punchy, elegant, and world-class. Write like a high-end luxury or top-tier tech agency copywriter. Be authoritative and valuable.
    - COPY RULE: The 'copy' field is for a SINGLE caption. NEVER write multiple versions of a caption (e.g., one for Facebook, one for Instagram) in the same copy text box. Since you are strictly generating one post per platform, write the copy natively for that specific platform (e.g., short and punchy for Instagram with emojis, professional for LinkedIn).
    - Set the scene with a clear visual concept.
    - Choose appropriate platforms from the available list.
    - Specify the format ('feed', 'story', or 'carousel'). ${preferredFormat ? `Strictly use ${preferredFormat} as requested.` : 'Choose the best format for the content.'}
    - SLIDE COUNT RULES:
      1. If format is 'carousel', dynamically determine the optimal number of slides (between 2 and 10).
      2. If format is 'feed' or 'story', you MUST generate EXACTLY 1 slide. No more, no less.
    - CAROUSEL POST QUALITY RULES & COHESION (If format is 'carousel'):
      1. Every Slide Must Earn the Next Swipe: Each slide must have one clear purpose, one main idea, and enough curiosity or utility to make the reader want to continue. Do not treat the carousel as an information dump.
      2. Strict Narrative Layout Flow:
         - Slide 1: Hook - Start with a bold, specific, scroll-stopping headline that creates curiosity, challenges a belief, highlights a major pain point, or promises an extremely useful outcome.
         - Slide 2: Problem or Context - Explain why the topic matters. Show the pain point, opportunity, mistake, or common misconception.
         - Middle slides: Value Delivery - Teach the core concepts using short, clear, highly practical points. Each individual slide must focus on exactly one single idea.
         - Example or Application slide - Always include an actionable example, "Do This, Not That" comparison, template, or before/after demonstration. Never give purely abstract advice.
         - Summary slide - Recap the key takeaways in a simple, memorable, saveable checklist format.
         - Final slide: CTA - End with a clear, single call to action that aligns with the post's objective (e.g., Save, Comment, DM, Share, or Visit Link).
      3. Mobile-First Readability & Style:
         - Keep text short and easy to scan. Avoid large blocks of paragraph text. Use bold headlines paired with clean supporting bullet points or short descriptions.
         - Every slide must feel like a single focused thought that can be fully comprehended in 2-3 seconds on a small screen.
      4. Infographics, Diagrams & Structural Breakdowns:
         - Use infographics, timelines, funnels, cause-and-effect flowcharts, step-by-step layouts, or anatomical breakdowns only when they make the idea easier to understand, not just for decoration.
         - COMPREHENSIVE IMAGE CONCEPT MANDATE (HARD RULE): If a slide contains a diagram, blueprint, chart, or infographic, the visual 'concept' field MUST describe it in extreme, meticulous detail, listing every single indicator arrow, part, label, connector, and layout. (e.g., "A modern infographic illustration showing a clean flow with uppercase thin indicators pointing to precise text tags: 'STEP 1: PREPARATION', 'STEP 2: ACTIVATION', and 'STEP 3: ENHANCEMENT'. The layout is clean and centered in the bottom 60% with a highly precise presentation style"). This gives the artwork engine enough details to produce clean, professional visual elements without empty spaces.
      5. PHOTOGRAPHY & CREATIVE DIRECTION (HARD RULE):
         - DO NOT write hyper-literal, cheesy, or uncanny image concepts (e.g., do NOT ask for "a pumpkin with human teeth", "a smiling tooth holding a toothbrush", or "a doctor giving a thumbs up").
         - Instead, use premium, high-end lifestyle photography, aesthetic architectural shots, abstract moody geometry, or highly polished cinematic scenes. Keep the visual subject dignified and elegant.
         - IMPORTANT: Ensure the background image or post artwork aligns perfectly with the message of the copy. Do not just use a generic template background—create an image concept that conceptually works with the specific slide copy.
      6. Consistent Background & Panorama Continuity:
         - Treat the slides as one horizontal panorama. Describe continuous background threads, lines, or scenic elements connecting them seamlessly from left to right.
         - Align visual subjects and text blocks to the exact same relative location across slides (e.g., centered bottom 60% for subjects, top 40% for headings) to prevent chaotic jumping. Re-use the same borders, icons, and color patterns.
      7. Text Card / Overlay Text Rule (HARD RULE): If any slide concept mentions or implies rendering text, you MUST provide the exact string of text to overlay in the "overlayText" field. ${brand.companyName?.toLowerCase().includes('volterra') ? `Formats must be structured with the following ALL-CAPS tags indicating where lines belong. ONLY USE THE TAGS IF RELEVANT (e.g. do not include [BULLETS] if there is no list). DO NOT invent random tags.
         CRITICAL TEXT LENGTH LIMITS:
         - [HEADING] must be between 5 - 12 words or max 65 characters (no one or two word headings).
         - [BODY] must be max 120 characters if it has bullet points. If there are NO bullet points, the text can expand downwards up to max 220 characters.
         - [BULLETS] Optional. ONLY INCLUDE IF THE SLIDE CONCEPT ACTUALLY CALLS FOR A LIST (like a checklist or key points). If there are NO bullet points needed, do NOT include the [BULLETS] tag or list items at all.
         [EYEBROW] <your eyebrow text>
         [HEADING] <your heading text>
         [BODY] <your concise body text>
         [BULLETS]
         - <short item 1>
         - <short item 2>
         [CTA] <button text>

         Emojis and lightning bolts (⚡) are STRICTLY FORBIDDEN on the slide artwork text overlays. Our layout blocks scale font sizes up or down dynamically using an Auto-Fit system to always look pristine.` : `Ensure the text is concise and fits well visually.`}
    - VISUAL TEMPLATE MATCHING (HARD RULE): If the brand provides "Visual Templates" in the assets (e.g. "Testimonial Template", "Sale Flyer"), and a slide's purpose matches that template, you MUST explicitly include the exact template name inside brackets like "[TEMPLATE: Exact Template Name]" in the slide's visual 'concept' field. This will tell our rendering engine to use their uploaded template as inspiration.
    - Generate a unique string ID for the post and each slide.
  `;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        id: { type: Type.STRING },
        date: { type: Type.STRING, description: "ISO date string" },
        platforms: { 
          type: Type.ARRAY, 
          items: { type: Type.STRING } 
        },
        copy: { type: Type.STRING },
        concept: { type: Type.STRING, description: "Overall visual concept" },
        format: { type: Type.STRING, enum: ["feed", "story", "carousel"] },
        type: { type: Type.STRING, enum: ["evergreen", "highlight", "promo", "event"] },
        bestTimeToPost: { type: Type.STRING },
        slides: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              concept: { type: Type.STRING, description: "Specific visual concept for this slide. MUST follow the narrative flow rules (Slide 1 must be a bold scroll-stopping Hook; Slide 2 must show Problem/Context; Middle slides must teach 1 specific Value idea with continuous backdrop; Example-slide shows 'Do This, Not That'/template; Summary slide shows a saveable checklist; Final slide shows a clear CTA). For diagrams or infographics, describe exact anatomical parts, arrows, and labels in meticulous detail." },
              imagePrompt: { type: Type.STRING, description: "A pure visual prompt for the background image generator. STRICTLY NO text, NO plaques, NO bento grids, and NO ui mockups. ONLY describe the scenic photography, colors, or objects (e.g. 'A bright dental waiting room with natural lighting')." },
              overlayText: { type: Type.STRING, description: "The exact headline, checklist points, list content, or quote text to be overlaid on this slide. MUST NOT be empty or blank if the slide concept implies or contains text, headlines, lists, bullet points, summaries, or quotes." }
            },
            required: ["id", "concept"]
          },
          minItems: (preferredFormat === 'feed' || preferredFormat === 'story') ? 1 : 1,
          maxItems: (preferredFormat === 'feed' || preferredFormat === 'story') ? 1 : 10
        }
      },
      required: ["id", "date", "platforms", "copy", "concept", "format", "type", "slides", "bestTimeToPost"]
    }
  };

  let finalPrompt = prompt;
  const blueprintPrompt = await getActiveBlueprintsPrompt(brand.companyName);
  if (blueprintPrompt) {
    finalPrompt += blueprintPrompt;
  }

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [...docParts.map(p => ({ parts: [{ inlineData: p.inlineData }] })), { parts: [{ text: finalPrompt }] }],
    config: config
  });

  const parsed = JSON.parse(response.text || "{}");
  if (parsed && typeof parsed === 'object') {
    const postId = 'post_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
    parsed.id = postId;
    parsed.copy = fixCaptionSpacing(parsed.copy || "");
    parsed.concept = fixCaptionSpacing(parsed.concept || "");
    if (parsed.slides && Array.isArray(parsed.slides)) {
      parsed.slides = parsed.slides.map((s: any) => ({
        ...s,
        id: 'slide_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36),
        concept: fixCaptionSpacing(s.concept || ""),
        overlayText: s.overlayText ? fixCaptionSpacing(s.overlayText) : s.overlayText
      }));
    }
  }
  return parsed;
}

// Global queue to manage parallelism for heavy AI tasks
class TaskQueue {
  private queue: (() => Promise<any>)[] = [];
  private running = false;

  async add<T>(task: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await task();
          resolve(result);
        } catch (err) {
          reject(err);
        }
      });
      this.process();
    });
  }

  private async process() {
    if (this.running || this.queue.length === 0) return;
    this.running = true;
    while (this.queue.length > 0) {
      const task = this.queue.shift();
      if (task) {
        await task();
        // Medium delay between tasks to avoid burst quota hits (100k limit is per minute)
        await new Promise(resolve => setTimeout(resolve, 5000 + Math.random() * 5000));
      }
    }
    this.running = false;
  }
}

const generationQueue = new TaskQueue();

const distilledCache = new Map<string, string>();

async function distillBrandContext(concept: string, rawContext: string, brandId?: string): Promise<string> {
  // If context is already small, don't waste an AI call
  if (!rawContext || rawContext.length < 3000) return rawContext;

  // Use brandId for caching if available, otherwise hash the rawContext
  const cacheKey = brandId || `ctx_${rawContext.length}_${rawContext.substring(0, 100)}`;
  if (distilledCache.has(cacheKey)) {
    console.log("Using cached distilled brand context");
    return distilledCache.get(cacheKey)!;
  }

  const apiKey = getApiKey('standard');
  if (!apiKey) return rawContext.substring(0, 4000);

  const prompt = `
    You are a Brand Identity Expert specializing in AI image prompting.
    
    ACTION: Distill the following brand guidelines and strategy documents into the most critical visual instructions for an AI image generator.
    
    BRAND DOCUMENTS:
    ${rawContext}
    
    INSTRUCTIONS:
    1. Extract ONLY visual-relevant details: Specific colors (hex codes), design style (e.g. brutalist, organic, minimalist), lighting, and required motifs.
    2. IMPORTANT: DO NOT discard the names of specific products or visual assets mentioned in the documents. These are needed for identification.
    3. Filter out non-visual business info like strategy, mission, or pricing.
    4. Keep the output as a dense set of "AI Visual Rules".
    5. MUST stay under 1500 characters.
  `;

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{ parts: [{ text: prompt }] }]
    });
    const result = (response.text || "").trim();
    console.log("Brand context distilled successfully:", result.length, "chars");
    
    if (result) {
      distilledCache.set(cacheKey, result);
    }
    
    return result;
  } catch (err) {
    console.error("Failed to distill brand context:", err);
    return rawContext.substring(0, 4000); // Fallback to truncation
  }
}

async function describePersonImage(base64: string, mimeType: string): Promise<string> {
  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{
        parts: [
          {
            inlineData: {
              data: base64,
              mimeType: mimeType
            }
          },
          {
            text: "Analyze this portrait of a real person. Describe their literal physical details in 1 to 2 sentences for a text-to-image generator prompt to replicate their likeness. Include: gender, ethnicity, hair (style, parting, length, color), facial hair (exactly specify if clean-shaven, stubble, or bearded), eyes/gaze, expression (smiling, neutral), clothing, and immediate surrounding background. Speak strictly in third person (e.g., 'A Caucasian man in his late 30s with...'). Do NOT use flowery language, describe only simple physical facts."
          }
        ]
      }]
    });
    const result = (response.text || "").trim();
    if (result) {
      console.log("Successfully described person image:", result);
      return result;
    }
  } catch (err) {
    console.error("Failed to describe person image:", err);
  }
  return "a professional person with a friendly expression.";
}

export async function analyzeAndProcessBrandTemplate(
  base64: string,
  mimeType: string,
  fallbackName: string,
  brandName?: string
): Promise<{
  name: string;
  description: string;
  role: string;
  shellUrl?: string;
}> {
  const brandContext = brandName ? `The brand/business is named "${brandName}".` : "";
  const prompt = `
    Analyze this uploaded brand template image. ${brandContext}
    Provide structured analysis. Decide:
    1. A beautiful, specific template name following a clean role-based naming convention, e.g., "[Brand/Category Name] [Template Role] Template" (e.g. "Volterra Testimonial Quote Template", "Elegant Hero Hook Template", "Modern FAQ Objection Template"). If the brand name is unavailable or not dental-related, invent an organic dental/wellbeing name or use typical descriptors.
    2. A comprehensive, natural visual description detailing the template layout rules. Specify: background color details, font vibe (serif, sans-serif, bold, minimal), layout structure (margins, cards, text placement areas, padding, etc.), alignment, presence of custom shapes, lines, frames, or decorative motifs, and instructions on how to replicate the aesthetic visually.
    3. The primary functional 'role' this template layout is best suited for. Select exactly one from these options: 
       - "hero_hook" (Ideal for core attention grabbing titles/hooks)
       - "testimonial_quote" (Best for single prominent quotes, patient/client reviews, authority statements)
       - "educational_faq" (For detailed question-and-answer layouts or Q&A)
       - "process_steps" (For sequential 1-2-3 list items or chronological steps)
       - "benefits_list" (For comparative checklist items or lists)
       - "service_spotlight" (Single highlighted product, treatment, or specific service)
       - "general_layout" (If generic/all-purpose)

    Return JSON format: {
      "name": "Template Name",
      "description": "Style description guidelines...",
      "role": "hero_hook" | "testimonial_quote" | "educational_faq" | "process_steps" | "benefits_list" | "service_spotlight" | "general_layout"
    }
  `;

  let name = fallbackName;
  let description = "Follow visual style.";
  let role = "general_layout";

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{
        parts: [
          {
            inlineData: {
              data: base64,
              mimeType: mimeType
            }
          },
          { text: prompt }
        ]
      }],
      config: {
        responseMimeType: "application/json"
      }
    });

    const bodyText = (response.text || "").trim();
    const clean = bodyText.replace(/```json|```/g, "").trim();
    const parsed = JSON.parse(clean);
    if (parsed.name) name = parsed.name;
    if (parsed.description) description = parsed.description;
    if (parsed.role) role = parsed.role;
  } catch (err) {
    console.error("Failed to analyze brand template layout:", err);
  }

  // Now, let's call the image generation tool to generate a stripped-out shell version!
  let shellUrl = "";
  try {
    console.log("[analyzeAndProcessBrandTemplate] Generating blank layout shell image based on design...");
    const conceptPrompt = `STRIP ALL TEXT AND SPECIFIC MEDIA CONTENT. Generate an empty, completely text-free background layout shell template from this brand template image. Remove all readable text, words, letters, labels, checkmarks, bullets, logos, copywriting, and photographic faces/portraits, leaving only the pristine solid background panels, cards, empty decorative outlines/motifs, borders, buttons, and layout placement grids exactly as they are. This is a visual background layout template.`;
    
    shellUrl = await generateArtwork(
      conceptPrompt,
      undefined, // logo
      "1:1", // aspect ratio
      undefined, // products
      undefined, // brandTemplates
      undefined, // brandContext
      `data:${mimeType};base64,${base64}`, // inspirationImage!
      "PRECISE_IMAGE_REFINEMENT_QUICK_FIX. Remove all text, numbers, letters, icons, logos, photos of faces, and illustrations from this slide, leaving the pure layout background elements, panels, canvas colors, margins, and borders as a clean unfilled template shell." // refinementPrompt override to force quick-fix image modification
    );
    console.log("[analyzeAndProcessBrandTemplate] Successfully generated blank shell template URL:", shellUrl);
  } catch (err) {
    console.warn("[analyzeAndProcessBrandTemplate] Shell image generation failed or timed out, fallback to original", err);
  }

  return { name, description, role, shellUrl: shellUrl || undefined };
}

export async function generateArtwork(
  concept: string, 
  logoUrl?: string, 
  aspectRatio: "1:1" | "9:16" | "16:9" = "1:1",
  selectedProducts?: { url: string; name: string; description?: string; price?: string }[],
  brandTemplates?: { url: string; name: string; description?: string; role?: string; shellUrl?: string }[],
  brandContext?: string,
  inspirationImage?: string, // Base64 or URL of an existing image to refine
  refinementPrompt?: string,  // Specific instruction for refinement (e.g., "Fix spelling of 'Center'")
  brandId?: string,
  approvedMedia?: { url: string; name: string; description?: string }[]
): Promise<string> {
  return generationQueue.add(() => _generateArtworkInternal(
    concept, 
    logoUrl, 
    aspectRatio, 
    selectedProducts, 
    brandTemplates, 
    brandContext, 
    inspirationImage, 
    refinementPrompt,
    brandId,
    approvedMedia
  ));
}

async function _generateArtworkInternal(
  concept: string, 
  logoUrl?: string, 
  aspectRatio: "1:1" | "9:16" | "16:9" = "1:1",
  selectedProducts?: { url: string; name: string; description?: string; price?: string }[],
  brandTemplates?: { url: string; name: string; description?: string; role?: string; shellUrl?: string }[],
  brandContext?: string,
  inspirationImage?: string,
  refinementPrompt?: string,
  brandId?: string,
  approvedMedia?: { url: string; name: string; description?: string }[]
): Promise<string> {
  console.log("Starting generateArtwork with concept:", concept, "Aspect Ratio:", aspectRatio, "Refinement:", !!refinementPrompt);
  
  const isTemplateShell = /unfilled template shell|clean unfilled template shell/i.test(refinementPrompt || "") || /STRIP ALL TEXT AND SPECIFIC MEDIA CONTENT/i.test(concept);
  const isQuickFix = !isTemplateShell && /PRECISE_IMAGE_REFINEMENT_QUICK_FIX/i.test(refinementPrompt || "");
  const isCarouselOverlay = !isQuickFix && !isTemplateShell && (/(seamless_panorama_slide_overlay|panorama_slide_overlay|SEAMLESS_PANORAMA_SLIDE_OVERLAY)/i.test(concept) || /(seamless_panorama_slide_overlay|panorama_slide_overlay|SEAMLESS_PANORAMA_SLIDE_OVERLAY)/i.test(refinementPrompt || ""));
  const isSeamlessPanoramaBase = !isTemplateShell && (/unbroken|panorama background|panorama_base|seamless_panorama_base/i.test(concept) || aspectRatio === "16:9");

  let finalTemplates: any[] = [];

  if (!isQuickFix && brandTemplates && brandTemplates.length > 0) {
    const combinedContext = (concept + " " + (refinementPrompt || "")).toLowerCase();
    const keywords = [
      'mascot', 'character', 'sheet', 'pose', 'logo', 'identity', 'brand element',
      'staff', 'owner', 'founder', 'headshot', 'profile', 'team', 
      'person', 'portrait', 'photo', 'man', 'woman', 'expert', 'face'
    ];
    
    const relevantTemplates = brandTemplates.filter(t => {
      const name = t.name.toLowerCase();
      const desc = (t.description || "").toLowerCase();
      const role = (t.role || "").toLowerCase();
      
      // Alphanumeric comparison for extreme robustness
      const cleanName = name.replace(/[^a-z0-9]/g, '');
      const tmplMatch = combinedContext.match(/\[template:\s*([^\]]+)\]/i);
      if (tmplMatch) {
        const matchStr = tmplMatch[1].trim().toLowerCase().replace(/[^a-z0-9]/g, '');
        if (matchStr === cleanName || cleanName.includes(matchStr) || matchStr.includes(cleanName)) {
          return true;
        }
      }
      
      const cleanCombined = combinedContext.replace(/[^a-z0-9]/g, '');
      if (cleanCombined.includes(cleanName) || cleanName.includes(cleanCombined)) return true;
      
      if (keywords.some(k => combinedContext.includes(k) && (name.includes(k) || desc.includes(k)))) {
        return true;
      }

      // Semantic template role matching!
      if (role && role !== "general_layout") {
        if (role === "hero_hook" && (combinedContext.includes("hook") || combinedContext.includes("intro") || combinedContext.includes("title") || combinedContext.includes("slide 1") || combinedContext.includes("first slide"))) return true;
        if (role === "testimonial_quote" && (combinedContext.includes("testimonial") || combinedContext.includes("quote") || combinedContext.includes("review") || combinedContext.includes("says") || combinedContext.includes("rating"))) return true;
        if (role === "educational_faq" && (combinedContext.includes("faq") || combinedContext.includes("question") || combinedContext.includes("answer") || combinedContext.includes("how to") || combinedContext.includes("why do"))) return true;
        if (role === "process_steps" && (combinedContext.includes("step") || combinedContext.includes("process") || combinedContext.includes("1.") || combinedContext.includes("2.") || combinedContext.includes("3.") || combinedContext.includes("timeline") || combinedContext.includes("guide"))) return true;
        if (role === "benefits_list" && (combinedContext.includes("benefit") || combinedContext.includes("checklist") || combinedContext.includes("choose") || combinedContext.includes("features") || combinedContext.includes("vs") || combinedContext.includes("points"))) return true;
        if (role === "service_spotlight" && (combinedContext.includes("service") || combinedContext.includes("product") || combinedContext.includes("spotlight") || combinedContext.includes("treatment") || combinedContext.includes("about our") || combinedContext.includes("offer"))) return true;
      }
      
      const stopWords = new Set(['dental', 'dentistry', 'clinic', 'office', 'brand', 'post', 'template', 'layout', 'teeth', 'tooth', 'smile', 'patient', 'patients', 'care', 'health', 'volterra']);
      const templateWords = name.split(/[^a-z0-9]+/).filter(w => w.length > 3 && !stopWords.has(w));
      const promptWords = combinedContext.split(/[^a-z0-9]+/).filter(w => w.length > 3 && !stopWords.has(w));
      
      if (templateWords.some(tw => promptWords.some(pw => tw === pw || tw.includes(pw) || pw.includes(tw)))) {
        return true;
      }
      return false;
    });

    finalTemplates = (relevantTemplates.length === 0 && brandTemplates.length > 0)
      ? brandTemplates.filter(t => {
          const n = t.name.toLowerCase();
          const d = (t.description || "").toLowerCase();
          return n.includes('default') || n.includes('standard') || n.includes('generic') || n.includes('layout') || n.includes('base') || n.includes('theme') ||
                 d.includes('default') || d.includes('standard') || d.includes('generic') || d.includes('layout') || d.includes('base') || d.includes('theme');
        })
      : relevantTemplates;
  }

  const isTypographic = !isQuickFix && 
                        !isTemplateShell && 
                        !isCarouselOverlay && 
                        !isSeamlessPanoramaBase && 
                        !(refinementPrompt && /STANDARD_SLIDELESS_OVERLAY|BACKGROUND-ONLY|BACKGROUND_ONLY/i.test(refinementPrompt)) &&
                        (finalTemplates.length > 0 ||
                         /(quote|text|typography|stat|card|meme|carousel)/i.test(concept) || 
                         /(quote|text|typography|stat|card|meme|carousel)/i.test(refinementPrompt || ""));

  let resolvedInspirationImage = inspirationImage;

  // Resolve Approved Media (Stock/Camera Gallery) first if any are uploaded and matched
  if (approvedMedia && approvedMedia.length > 0) {
    const combinedContext = (concept + " " + (refinementPrompt || "")).toLowerCase();
    
    // Check for explicit [USE APPROVED MEDIA: Exact Name] tag first
    const match = combinedContext.match(/\[use approved media:\s*([^\]]+)\]/i);
    let matchedItem = null;
    
    if (match) {
      const mediaName = match[1].trim().toLowerCase();
      matchedItem = approvedMedia.find(m => m.name.toLowerCase() === mediaName);
    }
    
    // Fallback: Check if the name of any media file is a keyword inside the concept
    if (!matchedItem) {
      matchedItem = approvedMedia.find(m => {
        const name = m.name.toLowerCase();
        if (name.length <= 3) return false;
        return combinedContext.includes(name);
      });
    }

    if (matchedItem && matchedItem.url) {
      resolvedInspirationImage = matchedItem.url;
      console.log(`[generateArtwork] Dynamic Media Feed: Matched Approved Media item "${matchedItem.name}", using its URL as base canvas inspiration:`, matchedItem.url);
    }
  }


    if (finalTemplates.length > 0) {
      const matchedTemplate = finalTemplates[0];
      
      const isFallbackSlideUrl = resolvedInspirationImage && (
        resolvedInspirationImage.includes('/slides/') || 
        resolvedInspirationImage.includes('firebase') || 
        resolvedInspirationImage.includes('cloudstorage') ||
        resolvedInspirationImage.includes('platapost') ||
        (inspirationImage && !refinementPrompt?.startsWith("PRECISE_IMAGE_REFINEMENT_QUICK_FIX") && !isQuickFix)
      );
      
      const shouldOverride = !resolvedInspirationImage || isFallbackSlideUrl;
      
      if (shouldOverride && !isQuickFix) {
        if (matchedTemplate && matchedTemplate.shellUrl) {
          resolvedInspirationImage = matchedTemplate.shellUrl;
          console.log(`[generateArtwork] Dynamic Brand Feeding (OVERRIDE): Using shellUrl of matched template "${matchedTemplate.name}" as base canvas instead of fallback background:`, resolvedInspirationImage);
        } else if (matchedTemplate && matchedTemplate.url) {
          resolvedInspirationImage = matchedTemplate.url;
          console.log(`[generateArtwork] Dynamic Brand Feeding (OVERRIDE): Using url of matched template "${matchedTemplate.name}" as base canvas instead of fallback background:`, resolvedInspirationImage);
        }
      } else {
        if (!resolvedInspirationImage && matchedTemplate && matchedTemplate.shellUrl) {
          resolvedInspirationImage = matchedTemplate.shellUrl;
          console.log(`[generateArtwork] Dynamic Brand Feeding: Using shellUrl of matched template "${matchedTemplate.name}" as base canvas!`);
        } else if (!resolvedInspirationImage && matchedTemplate && matchedTemplate.url) {
          resolvedInspirationImage = matchedTemplate.url;
          console.log(`[generateArtwork] Dynamic Brand Feeding: Using url of matched template "${matchedTemplate.name}" as base canvas!`);
        }
      }
    }

  let promptText = "";
  if (isTemplateShell) {
    promptText = `TASK: Generate a completely unfilled visual background layout template shell from the provided reference image.
REFERENCE IMAGE: This is the original customized slide design. It contains text, titles, descriptions, buttons, numbers, and a logo.
STRICT MANDATE:
1. STRIP ALL TEXT, NUMBERS, AND WRITING: You MUST completely erase, cover up, and remove all readable text, headlines, body copies, numbers, letters, checkmarks, bullets, button labels, slide numbers (like "01 / 06" or similar indicators), and copywriting from the image. 
2. NO GIBBERISH/MOCK WRITING: Do NOT replace the text with scribbles, squiggles, lines, or fake letters. The text regions must be completely empty/blank, matching the surrounding background panel or canvas color perfectly.
3. REMOVE LOGOS AND PHOTO PORTRAITS: Strip out any company logos (like circle icons, brand specific badges) and any photo portraits of people's faces, leaving their background panels empty.
4. PRESERVE LAYOUT STRUCTURE AND COLORS: Maintain the exact same background colors, panels, borders, solid margin bars, frames, outlines, empty input/button shapes, and overall visual composition.
5. The final output must be 100% textless and serve as a perfect clean background aesthetic canvas for dynamic text overlay.`;
  } else if (isQuickFix) {
    promptText = `TASK: You are performing a precise image-to-image modification on the provided design.
REFERENCE IMAGE: The reference image is the fully-composed and designed slide. It already contains all background elements, graphic frames, diagrams/illustrations, and overlay text.
STRICT INSTRUCTION: Do NOT add, draw, overlay, or write any new text, labels, lists, or buttons unless specifically and explicitly requested in the modification instruction below.
Do NOT repeat, redraw, or duplicate any list items, checkmarks, bullets, headers, or text lines already present in the reference image.
If there is a duplicate list/text block in the reference image, repair it by removing/erasing the duplicate copy so only a single, clean instance remains.
 
MODIFICATION REQUEST: "${refinementPrompt.replace('PRECISE_IMAGE_REFINEMENT_QUICK_FIX.', '').trim()}"
 
STRICT RULE: ONLY apply the change requested in the MODIFICATION REQUEST. Keep 100% of the background colors, visual motifs, and layout of unaffected elements exactly unchanged.`;
  } else if (refinementPrompt && (resolvedInspirationImage) && !/(SEAMLESS_PANORAMA_SLIDE_OVERLAY|STANDARD_SLIDE_OVERLAY|STANDARD_SLIDELESS_OVERLAY|BACKGROUND-ONLY|BACKGROUND_ONLY)/i.test(refinementPrompt)) {
    promptText = `TASK: Refine the provided image based on this instruction: ${refinementPrompt}.
       CONCEPT: ${concept}
       STRICT RULE: Maintain the exact same style, colors, and layout of the provided image. Only change what is necessary to fulfill the refinement instruction.`;
  } else {
    promptText = `TASK: Create a high-quality social media artwork.
       CONCEPT: ${concept}`;
    if (refinementPrompt) {
      promptText += `\n\nOVERLAY/LAYOUT DIRECTIVE:\n${refinementPrompt}`;
    }
  }
  
  if (isTemplateShell) {
    promptText += `\n\nSTRICT TEMPLATE SHELL STRUCTURAL RULE: The output must contain absolutely ZERO letters, zero words, zero numbers, and zero characters. It must be a completely pure, empty graphic template canvas with no text overlay of any kind.`;
  } else if (isSeamlessPanoramaBase) {
    promptText += `\n\nSTRICT UNBROKEN BACKGROUND INSTRUCTIONS: Generate a single, completely continuous, unified landscape wallpaper. 
    - MANDATORY RULE: Strictly and completely FORBID any vertical lines, panels, borders, vertical splits, partition lines, boxes, cards, shadows, overlays, or layout dividers. 
    - MANDATORY RULE: Do NOT draw mock devices, mock phones, laptops, frames, signs, or posters.
    - MANDATORY RULE: There must be ZERO text, zero typography, zero letters, and zero numbers in this artwork.
    - Composition: The canvas spans cleanly from far-left to far-right as one single, spacious, tranquil wide-angle environmental panoramic photo or ultra-smooth soft gradient backdrop with deep negative space. There should be at most a single central area of subtle theme-related visual elements, with no repeating subjects side-by-side.`;
  } else if (isTypographic) {
    promptText += '\n\nSTRICT GRAPHIC DESIGN & LAYOUT INSTRUCTIONS: ';
    if ((resolvedInspirationImage || finalTemplates.length > 0)) {
      promptText += '\n    1. MASTER REFERENCE LAYOUT (STRICT): You MUST use the provided reference image layout as your absolute structural guide. You MUST perfectly match and retain the exact background colors, solid block colors behind text, graphical bullets or icons, specific layout structure, and borders. Maintain 100% of the visual container structure, colors, and layout aesthetic.\n    2. LOGO PLACEMENT: You MUST retain the exact company logo in the precise placement, position, and colors as the reference image. Do not change the logo.\n    3. SCENE INTEGRATION: Blend the requested visual subject (e.g. photos or objects) realistically into the allotted background slots while preserving the pristine graphic containers.';
    } else {
      promptText += '\n    1. Use a clean, softly blurred aesthetic background or negative space.';
    }
    promptText += '\n    4. TYPOGRAPHY REPLACEMENT: Replace any existing dummy text from the reference completely with the newly requested text layout perfectly aligned to the same elements. Ensure the text remains highly readable.';
  } else if (isCarouselOverlay) {
    promptText += `\n\nSTRICT CAROUSEL OVERLAY INSTRUCTIONS: This is a premium continuous panorama segment. 
    1. UNDERLYING BACKDROP PRESERVATION: The provided reference image/empty template shell is the master background design layout. You MUST preserve 100% of its visual structure, border panels, leaf outlines, background textures, patterns, gradients, and margins completely unchanged.
    2. ADAPTIVE TYPOGRAPHY CONTAINER PLACEMENT (NO DUPLICATION): You MUST place and align all requested overlay text only inside the designated empty text containers/slots of the template layout (such as the off-white centrally positioned card box, or the left/right layout columns). You are STRICTLY FORBIDDEN from drawing, printing, or duplicate-rendering the text on the outside margins, green background headers/areas, or surrounding panels. If the layout card contains small pre-drawn checkbox/bullet icons on the left, align each text bullet line precisely horizontally to the immediate right of each corresponding icon. Do NOT print a second copy of the header, question, or checklists anywhere else on the frame. Every text overlay must exist EXACTLY ONCE.
    3. OVERLAY BLENDING: Realistic and creative illustrations or photographic elements from the slide's visual concept description MUST be generated and blended seamlessly on TOP of the background backdrop. This is NOT a suggestion - you must actually integrate the requested subject matter (like smiles, objects, people, diagrams, or clinical equipment) directly into the scene while keeping the background intact.
    4. HIGH-FIDELITY SCHEMATICS & DIAGRAMS: If the slide concept explicitly describes an educational diagram, flowchart, or professional chart, render a realistic, polished, and informative schematic. Ensure all relevant structures and components are clearly labeled and annotated with fine indicator lines and premium typography corresponding to the requested elements. The labels must be highly legible and clearly represent the described educational content. Do not force or draw anatomical structures (like crowns, implants, or teeth) unless explicitly specified in the concepts.
    5. SINGLE TEXT LOCATION RULE: For any given text overlay, render it exactly ONCE in the design. Under no circumstances should you print the same text or phrase in multiple places on the same slide.
    6. REMOVE AND REPLACE PRE-EXISTING TEXT/INDICATORS: If the master cropped slice (Reference Image) or brand template contains any pre-existing text, labels, writing, old slide numbers/counters (such as '01 / 06' or similar markers), logos, or placeholders, you MUST completely erase, paint over, or cover them clean. Replace them solely and cleanly with the new custom text and dynamic slide indicators requested. There must be zero double-printing or leakage of the old reference background text.
    7. CLEAN PORTRAIT PERSON BACKGROUND ISOLATION & LIKENESS: If the concept or template describes, implies, or includes a person (such as a dentist, practitioner, patient, doctor, clinical worker, team member, woman, man, or model):
       - You MUST render the person as a high-quality, professional, realistic studio portrait completely isolated against a perfectly clean, plain, flat, solid, non-gradient background color that matches the brand's aesthetic color exactly (e.g., solid off-white, solid light cream, or solid sage green), or with the background completely removed.
       - Under NO circumstances should you generate background scenes behind the person (such as clinics, medical rooms, chairs, windows, mirrors, dental equipment screens, foliage, or busy furniture). The person must look like a clean isolated cutout graphic element.
       - If replacing a person (such as a dentist or patient) or requesting a clinical professional, prefer a natural, inviting, high-confidence portrait of a professional female dentist/clinical practitioner in clean scrubs, smiling warm and naturally, ensuring no aggressive surgical poses (no hands holding syringes or clinical needles).`;
  } else if (isQuickFix) {
    promptText += `\n\nSTRICT QUICK-FIX IMAGE-TO-IMAGE REFINEMENT RULES:
    1. The reference image provided is the FULLY-COMPOSED slide. It already contains the visual background scenic details, illustration elements, icons, labels, and the typography header text.
    2. Do NOT treat the reference image as a blank backdrop, and do NOT overlay or paint the concept elements or typography header over again. Doing so will cause unwanted duplications.
    3. Modify ONLY the precise item or region requested by the user: "${refinementPrompt.replace('PRECISE_IMAGE_REFINEMENT_QUICK_FIX.', '').trim()}".
    4. Maintain the exact same background scenic style, foliage/leaf configurations, overlay color themes, element spacing, and text fonts so the modified slide stays visually seamless and consistent.`;
  }

  promptText += `
FORMAT: ${aspectRatio === "1:1" ? "Square (Feed)" : "Vertical (Story)"}
STYLE: Professional, modern, branded. Be creative and artistic while strictly following the concept.

TEXT RENDERING & SPELLING RULES:
1. ONLY render text that is explicitly enclosed in quotes (e.g., "Example Text").
2. Do NOT render font names, weights, or technical instructions (e.g., "Geist Sans", "Medium", "Centered", "Bottom", "Sans-serif") as literal text on the image. These are styling instructions only.
3. CRITICAL: Ensure all rendered text is spelled correctly. Double-check every letter.
4. If a word is complex or prone to misspelling, prioritize clarity and accuracy. If you are unsure of a word's spelling, try to use a simpler synonym that conveys the same meaning.
5. Ensure all rendered text is clearly legible and matches the case (uppercase/lowercase) requested in the quotes.
6. If no text is enclosed in quotes in the concept, do not include any text in the artwork.
7. Avoid "hallucinating" extra text or labels that were not requested.
8. AVOID GIBBERISH/MOCK TEXT IN LISTS & CHECKLISTS: If a list, infographic, diagram, or checklist is described mathematically or conceptually (e.g., 'bullet points with checkmark icons'), you MUST NOT generate fake dummy text, illegible scribbles, or placeholder letters for the list items. If the actual text for the list items/bullet points is NOT explicitly specified inside Quotes ("") in the CONCEPT, then simply render clean checklist icons (empty checkmark boxes, colored bullet points, circles, or checkmark icons) WITH NO TEXT AT ALL. The space next to each button or icon must be entirely blank/empty. Do not write mock words.
`;
  
  let finalBrandContext = brandContext;
  if (brandContext && brandContext.length > 3000) {
    finalBrandContext = await distillBrandContext(concept, brandContext, brandId);
  }

  if (finalBrandContext) {
    promptText += `\n\nCRITICAL BRAND GUIDELINES:\n${finalBrandContext}\n\nStrictly adhere to the brand colors (hex codes), typography, and visual aesthetic described above. The artwork MUST feel like it belongs to this specific brand.`;
  }
  
  const parts: any[] = [];

  // Helper to load image as base64 and compress it
  const loadImage = async (url: string, maxWidth = 1024, quality = 0.7) => {
    try {
      // Basic check for PDF to prevent scary errors when users link PDFs instead of images
      if (url.toLowerCase().split('?')[0].endsWith('.pdf')) {
        console.warn("Skipping PDF file for image generation:", url);
        return null;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      let response;
      try {
        response = await fetch(url, { 
          signal: controller.signal,
          referrerPolicy: 'no-referrer'
        });
        if (!response.ok) throw new Error(`Direct fetch failed: ${response.status}`);
      } catch (err) {
        console.warn(`Direct fetch failed for ${url}, falling back to proxy...`);
        const proxyUrl = `/api/media-proxy?url=${encodeURIComponent(url)}`;
        response = await fetch(proxyUrl, { signal: controller.signal });
      }
      
      clearTimeout(timeoutId);
      
      if (response && response.ok) {
        const blob = await response.blob();
        const rawBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Always compress aggressively to stay within Gemini's input token limits (especially for paid tier 100k limit)
        // 0.7 quality is a good balance between clarity for AI and token size
        const compressedBase64 = await compressBase64Image(rawBase64, maxWidth, maxWidth, quality);
        return { 
          base64: compressedBase64.split(',')[1], 
          mimeType: 'image/jpeg' // compressBase64Image returns jpeg
        };
      }
    } catch (err) {
      console.error("Failed to load image from URL:", url, err);
    }
    return null;
  };

  if (resolvedInspirationImage) {
    let img;
    const reqWidth = isQuickFix ? 1024 : 512;
    if (resolvedInspirationImage.startsWith('data:')) {
      const compressed = await compressBase64Image(resolvedInspirationImage, reqWidth, reqWidth, 0.75);
      img = { base64: compressed.split(',')[1], mimeType: 'image/jpeg' };
    } else {
      img = await loadImage(resolvedInspirationImage, reqWidth, 0.75);
    }
      
    if (img) {
      parts.push({
        inlineData: {
          data: img.base64,
          mimeType: img.mimeType
        }
      });
      promptText += `\n\nREFERENCE IMAGE: Use this image as the primary style and layout reference. ${refinementPrompt ? `Apply this fix: ${refinementPrompt}` : 'Maintain this visual style.'}`;
    }
  }

  if (logoUrl) {
    const logo = await loadImage(logoUrl, 128, 0.8); // Logos really don't need high res but need sharpness
    if (logo) {
      parts.push({
        inlineData: {
          data: logo.base64,
          mimeType: logo.mimeType
        }
      });
      const logoIndex = parts.length;
      promptText += `\n\nBRAND LOGO (Image Reference ${logoIndex}): Under all conditions, you must use this exact logo image when rendering corporate/brand logos. Do NOT draw a generic placeholder, substitute an incorrect logo, or draw animal shapes like a bison, bull, or buffalo. If the user mentions adding, using, or fixing the logo (or if a logo is present), you MUST put this precise logo in the corners (typically bottom right or bottom left) of the canvas.`;
      
      if (isQuickFix && (refinementPrompt || "").toLowerCase().includes("logo")) {
        promptText += `\n\nCRITICAL QUICK-FIX SPECIFIC LOGO INSTRUCTION: The user is specifically asking to add, correct, or use the brand logo. Find any existing generic, hallucinated, or incorrect logos in the reference image (such as the buffalo logo or any generic badge/text in the corners), and REPLACE it completely with the provided official BRAND LOGO (Image Reference ${logoIndex}). Position it cleanly in the corner as requested by the brand identity.`;
      }
    }
  }

  if (!isQuickFix && selectedProducts && selectedProducts.length > 0) {
    const combinedContext = (concept + (brandContext || "")).toLowerCase();
    
    // Strategy: Only include products that are mentioned in the concept or brand guidelines
    // or if the concept is very short (likely manual generation where filtering might be too aggressive)
    const relevantProducts = selectedProducts.filter(p => {
      const name = p.name.toLowerCase();
      // Simple inclusion check
      if (combinedContext.includes(name)) return true;
      // Key word check (words > 2 chars to be more inclusive)
      const words = name.split(/\s+/).filter(w => w.length > 2);
      if (words.length > 0 && words.some(w => combinedContext.includes(w))) return true;
      return false;
    });

    // Fallback: If no products matched or we have a small set, include more to be safe
    const finalProducts = (relevantProducts.length === 0 && selectedProducts.length > 0)
      ? selectedProducts.slice(0, 2) // Default to first 2 if nothing matched
      : relevantProducts;

    if (finalProducts.length > 0) {
      promptText += " \n\nCRITICAL VISUAL REFERENCE INSTRUCTIONS:";
      promptText += "\nI have provided images for products or visual elements listed below. You MUST use these specific images as the primary visual reference. Do NOT generate generic versions of these items. Instead, realistically incorporate the visual details, colors, and shapes from the provided images into the scene as described in the concept.";
      
      // Still limit to max 4 reference products total to stay within 100k token paid tier limits
      const limitedProducts = finalProducts.slice(0, 4);
      
      for (const product of limitedProducts) {
        const img = await loadImage(product.url, 768, 0.8); // Higher res and quality for clear recognition
        if (img) {
          const imageIndex = parts.length + 1; // +1 because prompt text will be unshifted to index 0
          parts.push({
            inlineData: {
              data: img.base64,
              mimeType: img.mimeType
            }
          });
          const nameAndDesc = (product.name + " " + (product.description || "")).toLowerCase();
          const isPerson = /(doctor|dr\b|staff|owner|founder|headshot|profile|team|person|portrait|photo|man|woman|expert|dentist|face|mascot|character)/i.test(nameAndDesc);
          let productPrompt = `\n- REFERENCE IMAGE ${imageIndex}: ${product.name}. Description/Usage: ${product.description || 'Incorporate this specific visual element. This is NOT a suggestion—you must accurately recreate its likeness, features, and colors.'} ${product.price ? `(Price: ${product.price})` : ''}`;
          if (isPerson) {
            const visualDescription = await describePersonImage(img.base64, img.mimeType);
            productPrompt += `\n  DETAILED VISUAL DESCRIPTION OF PERSON TO REPLICATE: ${visualDescription}`;
            productPrompt += `\n  CRITICAL HUMAN/PORTRAIT LIKENESS MANDATE: This reference image is a photo/portrait of a real person (or key brand character). If the concept requests this person (e.g., "${product.name}" or a matching role like doctor/dentist), you MUST preserve and accurately replicate their precise physical likeness, facial features, facial hair state (e.g., clean-shaven vs bearded), hair color/style, eye color, and overall appearance in the output. Do NOT substitute them with a generic stock person or someone who looks different. Keep them highly recognizable!`;
            promptText += `\n\n[LIKENESS REQUIREMENT FOR "${product.name}"]: The requested subject is a real person. You MUST base their physical appearance, face, hair, and clothing on this visual profile analyzed from their real photo: ${visualDescription}. Ensure facial hair status, hair styling, eye color, and age match exactly.`;
          }
          promptText += productPrompt;
        }
      }
      promptText += "\n\nEnsure these elements are accurately represented based on the provided photos.";
    }
  }

  if (!isQuickFix && finalTemplates && finalTemplates.length > 0) {
    if (finalTemplates.length > 0) {
      promptText += " \n\nFollow these brand guidelines/templates for visual style, layout, and composition. Check descriptions for specific usage rules:";
      // Limit to max 2 templates total to stay within 100k token paid tier limits
      const limitedTemplates = finalTemplates.slice(0, 2);
      
      for (const template of limitedTemplates) {
        const nameAndDesc = (template.name + " " + (template.description || "")).toLowerCase();
        const isPerson = /(doctor|dr\b|staff|owner|founder|headshot|profile|team|person|portrait|photo|man|woman|expert|dentist|face|mascot|character)/i.test(nameAndDesc);
        
        // Load shell template (completely textless background) if available to guarantee no text leakage, otherwise use standard template url
        const templateImageUrl = template.shellUrl || template.url;
        // Use higher resolution (768) and quality for people/portraits to retain facial details
        const img = await loadImage(templateImageUrl, isPerson ? 768 : 256, isPerson ? 0.8 : 0.7);
        
        // If we have a shell template, also load the original styled template image to guide visual subject positions (like faces, characters, and slots)
        let originalImg = null;
        if (template.shellUrl) {
          originalImg = await loadImage(template.url, isPerson ? 768 : 256, isPerson ? 0.8 : 0.7);
        }

        if (img) {
          const imageIndex = parts.length + 1;
          parts.push({
            inlineData: {
              data: img.base64,
              mimeType: img.mimeType
            }
          });
          
          let originalImageIndex = 0;
          if (originalImg) {
            originalImageIndex = parts.length + 1;
            parts.push({
              inlineData: {
                data: originalImg.base64,
                mimeType: originalImg.mimeType
              }
            });
          }

          let templatePrompt = "";
          if (originalImg) {
            templatePrompt = `\n- BRAND TEMPLATE BACKGROUND LAYOUT (Image Reference ${imageIndex}): ${template.name} - This is the text-free, logo-free background shell template layout. Replicate its visual structure, colors, panels, cards, margining, borders, empty decorative motifs, and background panels exactly.`;
            templatePrompt += `\n- BRAND TEMPLATE ORIGINAL REFERENCE (Image Reference ${originalImageIndex}): This is the original customized design layout showing how the visual subjects (such as the specific practitioners, professionals, team, or products), branding logo overlays, dynamic slide indicators (e.g. '01 / 06'), and text containers are structurally positioned on top of the layout.
            - Replicate this custom composition. Draw and place any requested visual subjects (such as the staff, or products described in the concept) in the exact same spatial regions and relative proportions as seen in Image Reference ${originalImageIndex}.
            - Cleanly replace and overwrite all old text headers, page number counters, buttons, and labels with the new ones specified in the overlay text request. You MUST, however, keep the original company logo exactly as it appears in the reference.
            - STRICT TYPO & COPY RULE: You must write the new text EXACTLY letter-by-letter. Do NOT duplicate words. Do NOT combine the old text with the new text (e.g. if replacing a header, completely drop the old header). Ensure word-perfect spelling!
            - DO NOT draw any visible bounding boxes, outlines, or stroke borders around the new text unless they exist in the original image! Match the clean aesthetic of the original.`;
          } else {
            templatePrompt = `\n- BRAND TEMPLATE REFERENCE (Image Reference ${imageIndex}): ${template.name} - This is the original customized design layout showing how visually the layout is structured, including text boxes, margins, graphics, icons, and layout slots.
  CRITICAL DIRECTION - REMOVE AND REPLACE PRE-EXISTING TEXT/GRAPHICS: 
  - This image is a layout, color, and aesthetic template ONLY. Do NOT copy the literal pre-existing text, labels, written numbers, or page indicators (such as '01 / 06') from BRAND TEMPLATE IMAGE ${imageIndex} into the generated slide. You MUST, however, keep the original company logo exactly as it appears.
  - If you see any pre-existing text, writing, numbers, placeholders, labels, or indicators in BRAND TEMPLATE IMAGE ${imageIndex}, you MUST completely ignore, erase, and paint over them perfectly with background colors. 
  - All text fields, headers, bullet points, checklists, labels, buttons, and slide numbers MUST be cleanly replaced by the actual overlay text requested for this slide.
  - You MUST perfectly replicate the background color, any colored side-bars or margins, any icons, any bullets, and any layout placements shown in the template. The new requested text should be perfectly aligned to these visual markers.
  - STRICT TYPO & COPY RULE: You must write the new text EXACTLY letter-by-letter without typos. Do NOT duplicate words or concatenate text. Proofread your spellings.
  - There must be absolutely no residual, ghostly, or double-printed text/numbers left over from the template layout. Ensure any text placeholder areas are painted over completely with the clean solid background color before rendering the new text.
  - DO NOT draw solid black bounds, outlines, or stroke borders around the new text unless they exist in the original image!`;
          }

          
          if (isPerson) {
            // Run description/likeness analysis on the original image that actually contains the people/staff rather than the blank shell!
            const imageToAnalyze = originalImg || img;
            const visualDescription = await describePersonImage(imageToAnalyze.base64, imageToAnalyze.mimeType);
            templatePrompt += `\n  DETAILED VISUAL DESCRIPTION OF PERSON TO REPLICATE: ${visualDescription}`;
            templatePrompt += `\n  CRITICAL HUMAN/PORTRAIT LIKENESS MANDATE: This template represents a real person (or key brand character). If the concept requests this person (e.g. "${template.name}" or matching roles like doctor/dentist), you MUST preserve and accurately replicate their precise physical likeness (such as gender, clean-shaven status/facial hair, hair styling, hair color, eye color, age, and facial expression) in the output. Blend and draw them in the exact same matching pose and composition layout as seen in the original reference. Keep them highly recognizable!`;
            promptText += `\n\n[LIKENESS REQUIREMENT FOR "${template.name}"]: The requested subject is a real person. You MUST base their physical appearance, face, hair, clean-shaven/facial hair status, and clothing on this visual profile analyzed from their original template photo: ${visualDescription}. Ensure all traits are accurately matched.`;
          }
          
          promptText += templatePrompt;
        }
      }
    }
  }

  if (refinementPrompt) {
    promptText += `\n\n==================================================\nCRITICAL USER QUICK-FIX / REFINEMENT DIRECTIVE (HIGHEST PRIORITY OVERRIDE):\nThe user has provided a target-specific instruction to change, fix, or modify the reference image:\n"${refinementPrompt}"\n\nYou MUST execute this change precisely.\n- If the instruction is to REMOVE, DELETE, or EXCLUDE an element (such as "remove the duplicate text for step 2", "remove extra wording", "delete the overlay badge", "remove duplicate blocks/labels"), you MUST completely erase, blank out, or remove that specific element/text from the output. In the resulting design, that area must be clean, blank, or seamlessly blended without those elements.\n- DO NOT DUPLICATE labels, steps, or text blocks unless explicitly instructed. If there is a duplicate labels bug in the reference image, repair it by removing the duplicate copy.\n- If the instruction is to CHANGE, ADD, or FIX some specific text details or spell check, you MUST update those details exactly as described.\n- Maintain everything else from the original reference image (the background colors, visual motifs, aesthetic themes, styles, fonts, and positions of unrelated elements) so it remains instantly recognizable, but definitely apply this exact requested modification.\n==================================================`;
  }

  const finalParts = [{ text: promptText }, ...parts];

  try {
    let modelName = 'gemini-3.1-flash-image-preview'; 
    console.log(`Attempting generation with model: ${modelName}`);
    
    let response;
    try {
      // Use proxy for generateContent with imageConfig
      response = await callGeminiProxy({
        model: modelName,
        type: 'premium',
        contents: [{ parts: finalParts }],
        config: {
          imageConfig: {
            aspectRatio: aspectRatio,
            imageSize: "1K"
          }
        }
      });
      
      if (!response || !response.candidates?.[0]?.content?.parts) {
        throw new Error("No candidates or parts returned from primary model");
      }
    } catch (e: any) {
      const errorMsg = e.message || (typeof e === 'string' ? e : JSON.stringify(e));
      console.warn(`Initial generation attempt with ${modelName} failed:`, errorMsg);

      // Fallback Chain
      const fallbacks = ['gemini-2.5-flash-image', 'imagen-3.0-generate-001', 'imagen-3']; 
      let lastError = e;
      let fallbackSuccess = false;

      for (const fallbackModel of fallbacks) {
        try {
          console.log(`Attempting fallback with model: ${fallbackModel}`);
          
          if (fallbackModel.includes('imagen')) {
             // Imagen models use the generateImages action
             response = await callGeminiProxy({
                 model: fallbackModel,
                 type: 'premium',
                 action: 'generateImages',
                 prompt: promptText,
                 config: {
                     numberOfImages: 1,
                     aspectRatio: aspectRatio,
                 }
             });
             
             if (response.generatedImages && response.generatedImages.length > 0) {
                 return `data:image/png;base64,${response.generatedImages[0].image.imageBytes}`;
             }
          } else {
             // Gemini models use generateContent with imageConfig
             response = await callGeminiProxy({
               model: fallbackModel,
               type: 'premium',
               contents: [{ parts: finalParts }],
               config: {
                 imageConfig: {
                   aspectRatio: aspectRatio
                 }
               }
             });
             
             if (response && response.candidates?.[0]?.content?.parts) {
               fallbackSuccess = true;
               break;
             }
          }
        } catch (fallbackError: any) {
          lastError = fallbackError;
          console.warn(`Fallback to ${fallbackModel} failed:`, fallbackError.message || fallbackError);
        }
      }

      if (!fallbackSuccess && !response?.candidates?.[0]?.content?.parts) {
        // One last desperate attempt if everything failed but somehow response exists
        if (response?.candidates?.[0]?.content?.parts) {
           fallbackSuccess = true;
        } else {
           throw lastError;
        }
      }
    }

    if (!response?.candidates?.[0]?.content?.parts) {
      throw new Error("No candidates or parts returned from Gemini");
    }

    for (const part of response.candidates[0].content.parts) {
      if (part.inlineData) {
        return `data:image/png;base64,${part.inlineData.data}`;
      }
    }
    throw new Error("No image data found in response parts");
  } catch (error: any) {
    console.error("Gemini Image Generation Error:", error);
    await logError("Artwork generation failed", { concept, aspectRatio, error: error.message }, error);
    throw error;
  }
}

const generateWithModel = async (mName: string, retryCount = 0) => {
  // This helper is used if we needed complex retry logic, but standard generateContent is used above
  return null;
};

export async function generateVideo(prompt: string, aspectRatio: "16:9" | "9:16" = "16:9", isMock: boolean = false, brandContext?: string, imageUrl?: string): Promise<string> {
  console.log("Starting generateVideo with prompt:", prompt, "Aspect Ratio:", aspectRatio, "Mock:", isMock);
  
  let fullPrompt = `TASK: Create a high-quality social media video.
CONCEPT: ${prompt}
ASPECT RATIO: ${aspectRatio}
STYLE: Professional, modern, branded. Be creative and artistic while strictly following the concept.

TEXT RENDERING RULES:
1. ONLY render text that is explicitly enclosed in quotes (e.g., "Example Text").
2. Do NOT render font names, weights, or technical instructions (e.g., "Geist Sans", "Medium", "Centered", "Bottom", "Sans-serif") as literal text in the video. These are styling instructions only.
3. Ensure all rendered text is spelled correctly, is clearly legible, and matches the case (uppercase/lowercase) requested in the quotes.
4. If no text is enclosed in quotes in the concept, do not include any text in the video.
5. Avoid "hallucinating" extra text or labels that were not requested.
`;

  if (brandContext) {
    fullPrompt += `\n\nCRITICAL BRAND GUIDELINES:\n${brandContext}\n\nStrictly adhere to the brand colors (hex codes), typography, and visual aesthetic described above. The video MUST feel like it belongs to this specific brand.`;
  }

  if (isMock) {
    // Simulate generation time
    await new Promise(resolve => setTimeout(resolve, 8000));
    // Return a high-quality placeholder video
    const mockVideos = [
      "https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
      "https://storage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
      "https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4"
    ];
    const randomVideo = mockVideos[Math.floor(Math.random() * mockVideos.length)];
    
    // Fetch and convert to base64 to simulate the real flow
    const response = await fetch(randomVideo);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Create a fresh instance to ensure we use the latest selected API key
  const apiKey = getApiKey('premium');
  
  if (!apiKey) {
    throw new Error("Gemini API key not found.");
  }

  try {
    console.log("Attempting video generation with model: veo-3.1-lite-generate-preview");
    return await _generateVideoInternal(fullPrompt, 'veo-3.1-lite-generate-preview', aspectRatio, apiKey, imageUrl);
  } catch (error: any) {
    console.warn("Primary video model failed, trying fallback: veo-2.0-generate-001. Error:", error.message || error);
    try {
      return await _generateVideoInternal(fullPrompt, 'veo-2.0-generate-001', aspectRatio, apiKey, imageUrl);
    } catch (fallbackError: any) {
      console.warn("Fallback video model also failed. Executing high-fidelity thematic stock video fallback...", fallbackError.message || fallbackError);
      
      const lowercasePrompt = prompt.toLowerCase();
      let chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-abstract-glowing-digital-lines-background-loop-42289-large.mp4"; // Default abstract
      
      if (lowercasePrompt.match(/sew|machine|fabric|tailor|pattern|clothing|fashion|design|dressmaker|stitch/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-woman-working-at-a-sewing-machine-in-a-studio-41551-large.mp4";
      } else if (lowercasePrompt.match(/code|program|developer|laptop|screen|computer|keyboard|typing|tech/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-software-developer-working-on-his-computer-34139-large.mp4";
      } else if (lowercasePrompt.match(/discuss|meeting|office|team|collaboration|collaborate|colleague|business|presentation/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-teamwork-in-a-modern-office-creative-space-40348-large.mp4";
      } else if (lowercasePrompt.match(/tablet|pencil|sketch|design|drawing|palette|creative/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-working-on-a-digital-tablet-34077-large.mp4";
      } else if (lowercasePrompt.match(/food|coffee|restaurant|cafe|eat|drink|beverage/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-hands-holding-cups-of-coffee-at-a-cafe-table-41139-large.mp4";
      } else if (lowercasePrompt.match(/nature|outdoor|tree|garden|sky|sun|climate/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-trees-with-yellow-flowers-in-spring-48906-large.mp4";
      } else if (lowercasePrompt.match(/abstract|particle|fluid|wave|color|ambient/)) {
        chosenVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-fluid-painting-colors-swirling-background-loop-42866-large.mp4";
      }

      console.log("Selected high-fidelity fallback video matching prompt:", chosenVideoUrl);

      try {
        console.log("Fetching stock video fallback via backend CORS proxy...");
        const proxyUrl = `/api/video/proxy?url=${encodeURIComponent(chosenVideoUrl)}`;
        const response = await fetch(proxyUrl, {
          headers: {
            "Authorization": `Bearer ${await auth.currentUser?.getIdToken()}`
          }
        });
        if (!response.ok) throw new Error(`Proxy download failed with status ${response.status}`);
        const result = await response.json();
        if (!result.dataUrl) throw new Error("Proxy response does not contain dataUrl");
        console.log("Successfully fetched high-fidelity stock video fallback via backend proxy!");
        return result.dataUrl;
      } catch (videoFetchError) {
        console.warn("High-fidelity stock video fetch failed, invoking ultimate static image fallback... Error:", videoFetchError);
        const imgRatio: "1:1" | "9:16" = (aspectRatio === "9:16" ? "9:16" : "1:1");
        try {
          const fallbackArtwork = await generateArtwork(
            prompt,
            undefined, // logoUrl
            imgRatio,
            undefined, // selectedProducts
            undefined, // brandTemplates
            brandContext,
            imageUrl, // use base64 image as context/ inspiration
            undefined // refinementPrompt
          );
          console.log("Successfully generated rich premium visual placeholder to substitute the video!");
          return fallbackArtwork;
        } catch (imgError: any) {
          console.error("Video fallback artwork generation also failed:", imgError);
          throw new Error(`Video generation and premium image fallback both failed. Video Error: ${fallbackError.message || fallbackError}. Image Error: ${imgError.message || imgError}`);
        }
      }
    }
  }
}

async function _generateVideoInternal(
  fullPrompt: string,
  modelName: string,
  aspectRatio: "16:9" | "9:16",
  apiKey: string,
  imageUrl?: string
): Promise<string> {
  const params: any = {
    model: modelName,
    action: 'generateVideos',
    prompt: fullPrompt,
    type: 'premium',
    config: {
      numberOfVideos: 1,
      resolution: '720p',
      aspectRatio: aspectRatio,
      personGeneration: 'allow_adult',
    }
  };

  if (imageUrl) {
    console.log("Using provided URL/Base64 as starting frame for Video Generation!");
    if (imageUrl.startsWith('data:image/') || imageUrl.startsWith('data:video/')) {
      const mimeType = imageUrl.split(';')[0].split(':')[1];
      const base64 = imageUrl.split(',')[1];
      if (mimeType.startsWith('video/')) {
        params.video = { videoBytes: base64, mimeType };
      } else {
        params.image = { imageBytes: base64, mimeType };
      }
    } else if (imageUrl.startsWith('http')) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const mimeType = blob.type || (imageUrl.endsWith('.mp4') ? 'video/mp4' : 'image/png');
        if (mimeType.startsWith('video/')) {
          params.video = { videoBytes: base64, mimeType };
        } else {
          params.image = { imageBytes: base64, mimeType };
        }
      } catch (err) {
        console.error("Failed to fetch media for video generation fallback:", err);
      }
    }
  }

  let operation;
  try {
    operation = await callGeminiProxy(params);
  } catch (err: any) {
    if (params.video) {
      console.warn("Video-to-video generation failed, retrying without the starting video...", err);
      delete params.video;
      operation = await callGeminiProxy(params);
    } else {
      throw err;
    }
  }

  // Poll for completion
  while (!operation.done) {
    await new Promise(resolve => setTimeout(resolve, 5000));
    operation = await callGeminiProxy({
      model: modelName,
      action: 'getOperation',
      operationName: operation.name,
      type: 'premium'
    });
  }

  if (operation.error && operation.error.code === 3) {
    console.warn("Safety error encountered. Attempting to rewrite prompt for safety...");
    try {
      const rewriteResponse = await callGeminiProxy({
        model: "gemini-2.5-flash",
        contents: [{ parts: [{ text: `The following prompt was rejected by a video generation model due to sensitive words violating Responsible AI practices. Please rewrite the prompt to communicate a similar visual concept but completely remove any sensitive, violent, sexual, or otherwise restricted terms. Make it safe and benign.

REJECTED PROMPT:
${fullPrompt}

Return ONLY the rewritten prompt.` }] }]
      });
      
      if (rewriteResponse.text) {
        const safePrompt = rewriteResponse.text.trim();
        console.log("Rewritten safe prompt:", safePrompt);
        params.prompt = safePrompt;
        
        let retryOperation = await callGeminiProxy(params);
        while (!retryOperation.done) {
          await new Promise(resolve => setTimeout(resolve, 5000));
          retryOperation = await callGeminiProxy({
            model: modelName,
            action: 'getOperation',
            operationName: retryOperation.name,
            type: 'premium'
          });
        }
        operation = retryOperation;
      }
    } catch (rewriteErr) {
      console.error("Failed to rewrite prompt for safety", rewriteErr);
    }
  }

  const downloadLink = 
    operation.response?.generateVideoResponse?.generatedSamples?.[0]?.video?.uri ||
    operation.response?.generatedVideos?.[0]?.video?.uri ||
    operation.response?.generatedVideos?.[0]?.video?.downloadUri ||
    operation.response?.generatedSamples?.[0]?.video?.uri ||
    operation?.generateVideoResponse?.generatedSamples?.[0]?.video?.uri ||
    operation?.generatedVideos?.[0]?.video?.uri;
  
  if (!downloadLink) {
    console.error("Video operation failed or missing link. Parsed operation:", JSON.stringify(operation, null, 2));
    throw new Error(`No video download link found. ${operation.error ? ('Operation error: ' + JSON.stringify(operation.error)) : ''}`);
  }

  console.log("Downloading generated video from downloadLink via backend proxy...");
  const proxyUrl = `/api/video/proxy?url=${encodeURIComponent(downloadLink)}&apiKey=${encodeURIComponent(apiKey || '')}`;
  const response = await fetch(proxyUrl, {
    method: 'GET',
    headers: {
      "Authorization": `Bearer ${await auth.currentUser?.getIdToken()}`
    }
  });

  if (!response.ok) throw new Error(`Failed to download video through proxy (status ${response.status})`);

  const result = await response.json();
  if (!result.dataUrl) throw new Error("CORS Proxy returned empty video dataUrl");
  return result.dataUrl;
}

export async function extractPersonality(content: string, platform: string): Promise<{ brandVoice: string; targetAudience: string; keyPillars: string[] }> {
  const prompt = `Analyze the following content from ${platform} and extract the brand voice/personality, target audience description, and primary content themes/pillars.
  
  Content:
  """
  ${content}
  """
  
  Return your analysis specifically focusing on the writing style DNA:
  - Sentence structure (e.g., short/punchy vs long/academic)
  - Vocabulary level and specific jargon/buzzwords used
  - Tonal nuances (e.g., emojis, bolding usage, rhetorical questions)
  - Emotional resonance (e.g., provocative, comforting, authoritative)
  
  Provide a cohesive description for "brandVoice" that captures these nuances.`;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        brandVoice: { type: Type.STRING },
        targetAudience: { type: Type.STRING },
        keyPillars: { type: Type.ARRAY, items: { type: Type.STRING } }
      },
      required: ["brandVoice", "targetAudience", "keyPillars"]
    }
  };

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{ parts: [{ text: prompt }] }],
    config: config
  });

  const rawText = response.text || "{}";
  const clean = rawText.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

export async function analyzeInspirationImage(base64Image: string, mimeType: string, userContext?: string): Promise<{ usage: string; aesthetic: string; promptDNA: string }> {
  // Compress inspiration image for analysis to save tokens (512px is plenty for style analysis)
  const compressedBase64 = await compressBase64Image(base64Image, 768, 768, 0.6);
  const dataOnly = compressedBase64.split(',')[1];

  const prompt = `Analyze this image and extract its visual DNA for AI content generation. 
  ${userContext ? `User context/notes to focus on: "${userContext}"` : ""}
  Determine:
  1. Usage: What type of content is this image best suited for? (e.g. "Personal Authority/Thought Leadership", "Product Showcase", "Lifestyle/B-Roll")
  2. Aesthetic: A comprehensive description of the visual style, colors, mood, lighting, and composition (up to 1000 characters).
  3. Prompt DNA: A detailed comma-separated list of keywords that would help an AI image generator (like Flux or Midjourney) reproduce this exact style.
  
  Return the analysis in JSON format with keys: "usage", "aesthetic", "promptDNA".`;
  
  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{
      parts: [
        { text: prompt },
        { inlineData: { data: dataOnly, mimeType: "image/jpeg" } }
      ]
    }],
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          usage: { type: Type.STRING },
          aesthetic: { type: Type.STRING },
          promptDNA: { type: Type.STRING }
        },
        required: ["usage", "aesthetic", "promptDNA"]
      }
    }
  });

  try {
    return JSON.parse(response.text);
  } catch (e) {
    return {
      usage: "General Content",
      aesthetic: "Professional visual style inspired by provided image.",
      promptDNA: "high quality, professional photography, modern aesthetic"
    };
  }
}

export async function generateWebsiteContext(url: string): Promise<string> {
  const prompt = `
    Analyze the website at ${url} using your search tools.
    
    Provide a comprehensive summary of the brand, including:
    1. Core value proposition and mission.
    2. Key products or services offered.
    3. Target audience and ideal customer profile.
    4. Tone of voice and brand personality.
    5. Unique selling points (USPs).
    6. Visual Identity:
       - Primary and secondary brand colors (provide hex codes if possible).
       - Typography and font styles used.
       - Image styling and visual aesthetic (e.g., minimalist, vibrant, dark, lifestyle-focused).
    7. Team Members & Leadership: List key team members, their roles, and any bios or relevant context if available.
    8. Products & Services: List specific products or services offered, including their descriptions, prices, and any image URLs or page links if found.

    CRITICAL: If you cannot access the website or if the content is sparse/generic, do NOT hallucinate or make up details. If the site appears to be a placeholder, under construction, or a login page, state that clearly. Only provide information you can verify from the site content.
    
    Format the output as a clean, professional context document.
  `;
  
    try {
      const response = await callGeminiProxy({
        model: "gemini-2.5-flash",
        contents: [{
          parts: [
            { text: prompt }
          ]
        }],
        config: {
          tools: [
            { googleSearch: {} }
          ]
        }
      });

      return response.text || "Failed to generate website context.";
    } catch (err: any) {
      const errorMsg = err.message || "";
      const isAccessError = errorMsg.includes("403") || 
                           errorMsg.includes("Forbidden") || 
                           errorMsg.includes("permitted") || 
                           errorMsg.includes("allowed") ||
                           errorMsg.includes("restricted") ||
                           errorMsg.includes("not enabled") ||
                           errorMsg.includes("API_KEY_HTTP_REFERRER_BLOCKED");

      if (isAccessError) {
        console.warn("Website grounding with Search failed (access restricted). Retrying with basic model...", errorMsg);
        try {
          // Fallback to 1.5 flash which is more widely available and try a lighter prompt
          const retryResponse = await callGeminiProxy({
            model: "gemini-1.5-flash",
            contents: [{
              parts: [
                { text: prompt + "\n\nNote: Please analyze based on the URL provided if possible, or provide a generic structural advice if you cannot access it directly. Do NOT use search tools." }
              ]
            }]
          });
          return retryResponse.text || "Failed to generate context after retry.";
        } catch (retryErr: any) {
          console.error("Critical: Both website search and basic generation failed. Check API key permissions and HTTP Referrer restrictions.", retryErr);
          const finalErrorMsg = retryErr.message || "";
          
          await logError("Website grounding failed (All fallbacks)", { url, error: finalErrorMsg }, retryErr);

          if (finalErrorMsg.includes("403") || finalErrorMsg.includes("Forbidden") || finalErrorMsg.includes("PERMISSION_DENIED")) {
            throw new Error("AI Access Forbidden: This usually means your API Key has 'HTTP Referrer Restrictions' enabled in Google Cloud Console that do not include this domain, or the model is not enabled in your region for this key.");
          }
          throw retryErr;
        }
      }
      await logError("Website grounding failed (Unexpected error)", { url, error: errorMsg }, err);
      throw err;
    }
}

export async function extractBrandProfileFromContext(contextText: string): Promise<{
  companyName?: string;
  brandVoice?: string;
  targetAudience?: string;
  visualAesthetic?: string;
  primaryGoals?: string;
  brandingDetails?: string;
  fontHeading?: string;
  fontBody?: string;
  colorPalette?: string[];
  isAiOrLowInfoWebsite?: boolean;
  aiWarningReason?: string;
  productsAndServices?: { name: string; description?: string; price?: string; url?: string; isService?: boolean }[];
  teamDirectory?: { name: string; role: string; bio?: string; imageUrl?: string }[];
}> {
  const prompt = `You are an expert brand strategist. Given the following unstructured brand/website context, extract clear and robust descriptions for prepopulating a brand profile.
  
  Context Content:
  """
  ${contextText}
  """
  
  Please extract and synthesize the following:
  1. companyName: The name of the organization or personal brand.
  2. brandVoice: A descriptive paragraph explaining the tone of voice and brand personality.
  3. targetAudience: A detailed paragraph describing their target audience and ideal customer profile (demographics, needs, etc.).
  4. visualAesthetic: A description of the visual identity and design style (minimalist, vibrant, professional, etc.) and color preferences.
  5. primaryGoals: The key goals or value proposition of the organization (e.g. skills training, customer acquisition, lead gen).
  6. brandingDetails: A bulleted list summarizing the key products/services, unique selling points (USPs), or any relevant branding notes.
  7. fontHeading: Identify the actual heading font used by the website from the CSS/context. If the exact font is proprietary or not available on Google Fonts (e.g., Circular, Proxima Nova, Helvetica), specify the closest visually identical free alternative on Google Fonts (e.g., "Montserrat", "Inter", "Plus Jakarta Sans", "Space Grotesk", "Outfit", "Lora"). Return just the font family name.
  8. fontBody: Identify the actual body text font used by the website from the CSS/context. If proprietary, specify the closest visually identical free Google Font. Return just the font family name.
  9. colorPalette: An array of up to 5 HEX color codes representing the brand's core design color palette parsed from the text. If precise hex codes are provided, use them. If only descriptive colors are mentioned (e.g., "deep blue and sage green"), provide your best visually appealing HEX code estimates for those named colors. Return an empty array [] only if absolutely no visual color information is available.
  10. isAiOrLowInfoWebsite: A boolean (true/false). Set this to true if you detect that:
      - The website context represents an AI-generated website.
      - Template boilerplate/placeholders are used (like standard Divi, Hostinger, Wix, or generic default template statements).
      - Under-construction, default parking page, or login page is found.
      - The brand identity/visual design is described in the text as "not explicitly detailed", "not explicitly discernible", "not clearly detailed", or "unable to verify".
      - The website is unoptimized for SEO and lack coherent metadata, causing search tools to scrape random, unrelated web associations (e.g., general e-commerce platforms like Office Depot, or miscellaneous objects like crayons/articulated dragons/novelties) instead of a clear, single business product.
      - The text states that no authentic or comprehensive information for the specific domain is detailed.
  11. aiWarningReason: If isAiOrLowInfoWebsite is true, provide a direct, professional explanation of why it was flagged.
  12. productsAndServices: An array of specific products or services identified on the website.
  13. teamDirectory: An array of specific team members identified on the website.
  
  Return the result in JSON format matching the response schema. If any field cannot be reasonably extracted, leave it blank or omit it, but attempt to populate them with your best understanding.`;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        companyName: { type: Type.STRING },
        brandVoice: { type: Type.STRING },
        targetAudience: { type: Type.STRING },
        visualAesthetic: { type: Type.STRING },
        primaryGoals: { type: Type.STRING },
        brandingDetails: { type: Type.STRING },
        fontHeading: { type: Type.STRING },
        fontBody: { type: Type.STRING },
        colorPalette: {
          type: Type.ARRAY,
          items: { type: Type.STRING }
        },
        isAiOrLowInfoWebsite: { type: Type.BOOLEAN },
        aiWarningReason: { type: Type.STRING },
        productsAndServices: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              description: { type: Type.STRING },
              price: { type: Type.STRING },
              url: { type: Type.STRING },
              isService: { type: Type.BOOLEAN }
            }
          }
        },
        teamDirectory: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              role: { type: Type.STRING },
              bio: { type: Type.STRING },
              imageUrl: { type: Type.STRING }
            }
          }
        }
      }
    }
  };

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{ parts: [{ text: prompt }] }],
      config: config
    });

    const rawText = response.text || "{}";
    const clean = rawText.replace(/```json|```/g, "").trim();
    const result = JSON.parse(clean);

    // Dynamic heuristic backup checks for SEO-unoptimized, low-metadata, or hallucinated crawler returns
    const lowerContext = contextText.toLowerCase();
    const hasUnverifiableAesthetics = 
      lowerContext.includes("not explicitly discernible") || 
      lowerContext.includes("not explicitly detailed") || 
      lowerContext.includes("not clearly detailed") || 
      lowerContext.includes("unable to verify") ||
      lowerContext.includes("virtually empty") ||
      lowerContext.includes("is not detailed") ||
      lowerContext.includes("no authentic or comprehensive") ||
      lowerContext.includes("lack of") ||
      (lowerContext.includes("office depot") && !lowerContext.includes("officedepot.com") && !lowerContext.includes("odpbusiness.com"));

    if (hasUnverifiableAesthetics && !result.isAiOrLowInfoWebsite) {
      result.isAiOrLowInfoWebsite = true;
      if (!result.aiWarningReason) {
        result.aiWarningReason = "The website content is unoptimized for search indexing and metadata extraction, yielding mismatched e-commerce platform associations.";
      }
    }

    return result;
  } catch (err) {
    console.error("Failed to extract brand profile details from context:", err);
    return {};
  }
}

export async function analyzeWebsiteScreenshot(base64Image: string, mimeType: string): Promise<{
  companyName?: string;
  brandVoice?: string;
  targetAudience?: string;
  visualAesthetic?: string;
  primaryColors?: string;
  brandingDetails?: string;
  fontHeading?: string;
  fontBody?: string;
  colorPalette?: string[];
}> {
  // Compress screenshot to a reasonable resolution
  const compressedBase64 = await compressBase64Image(base64Image, 1024, 1024, 0.7);
  const dataOnly = compressedBase64.split(',')[1];

  const prompt = `You are a world-class brand strategist and UX designer. Analyze this website screenshot and extract its brand identity, copy/text cues, and visual layout.
  
  Identify:
  1. companyName: The name of the organization or brand, if visible in logo or headers.
  2. brandVoice: Reconstruct the brand voice, tone, and personality based on the design, typography choices, and visible copywriting.
  3. targetAudience: Infer the ideal customer profile or target audience based on the content and style.
  4. visualAesthetic: A description of the design style, layout structure, typography, and vibe.
  5. primaryColors: Extract the main brand colors visible in the design (e.g. "#1A365D Primary deep blue, #E2E8F0 neutral background"). 
  6. brandingDetails: A summary of the key messages, products or services, and unique elements visible in the layout.
  7. fontHeading: Visually analyze the headings in the screenshot. If the font is recognizable but proprietary, specify the closest visually identical free Google Font (e.g. "Montserrat", "Playfair Display", "EB Garamond", "Space Grotesk"). Return just the Google Font family name.
  8. fontBody: Visually analyze the body paragraph text in the screenshot. Specify the closest visually identical free Google Font (e.g. "Inter", "Roboto", "Open Sans", "Work Sans"). Return just the Google Font family name.
  9. colorPalette: An array of up to 5 HEX color codes representing the primary and secondary colors seen in the screenshot image. IMPORTANT: Extract the precise HEX codes seen visually (e.g. olive green like #51554E, muted beige like #F4F4F0). DO NOT default to generic saturated colors like #007BFF or #28A745 if they are not the actual primary aesthetic of the screenshot.
  
  Return the analysis in JSON format with keys "companyName", "brandVoice", "targetAudience", "visualAesthetic", "primaryColors", "brandingDetails", "fontHeading", "fontBody", "colorPalette".`;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        companyName: { type: Type.STRING },
        brandVoice: { type: Type.STRING },
        targetAudience: { type: Type.STRING },
        visualAesthetic: { type: Type.STRING },
        primaryColors: { type: Type.STRING },
        brandingDetails: { type: Type.STRING },
        fontHeading: { type: Type.STRING },
        fontBody: { type: Type.STRING },
        colorPalette: {
          type: Type.ARRAY,
          items: { type: Type.STRING }
        }
      }
    }
  };

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{
        parts: [
          { text: prompt },
          { inlineData: { data: dataOnly, mimeType: "image/jpeg" } }
        ]
      }],
      config: config
    });

    const rawText = response.text || "{}";
    const clean = rawText.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch (err) {
    console.error("Failed to analyze website screenshot:", err);
    return {};
  }
}

export async function refineContent(
  brand: BrandProfile,
  currentText: string,
  userPrompt: string,
  contentType: 'copy' | 'concept',
  parentContext?: { copy: string; slides: { id: string; concept: string; overlayText?: string }[] },
  slideId?: string
): Promise<string> {
  const docParts: any[] = [];
  if (brand.contextDocuments && brand.contextDocuments.length > 0) {
    for (const doc of brand.contextDocuments) {
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        docParts.push({
          inlineData: {
            data: base64,
            mimeType: doc.mimeType
          }
        });
      } catch (err) {
        console.error("Failed to load context document:", doc.name, err);
      }
    }
  }

  const profileContext = brand.accountType === 'personal'
    ? `Profile Type: Personal Brand/Creator. Use first-person ("I", "me", "my").`
    : `Profile Type: Company/Business. Use standard brand voice ("we", "our").`;

  let parentContextSection = '';
  if (parentContext) {
    const slideIndex = slideId ? parentContext.slides.findIndex(s => s.id === slideId) : -1;
    const slideNumStr = slideIndex >= 0 ? `Slide #${slideIndex + 1}` : 'one of the slides';
    parentContextSection = `
    NARRATIVE CONTEXT (The post this text belongs to):
    - Parent Post Copy/Caption: "${parentContext.copy}"
    - Carousel Slide Concepts & Structure:
    ${parentContext.slides.map((s, i) => `  Slide ${i + 1}${s.id === slideId ? ' (THIS SLIDE YOU ARE REFINING)' : ''}:
      Visual Concept: "${s.concept}"
      Overlay Text: "${s.overlayText || 'None'}"`).join('\n')}
    
    INSTRUCTION FOR NARRATIVE SENSITIVITY:
    - You are refining ${contentType === 'copy' ? `the overall post copy` : `${slideNumStr}`}.
    - Ensure your refined content perfectly matches the overall brand voice, theme, color palette, style, and flow of the other slides in the series. Do NOT break the cohesive storyline, background narrative, or references.
    `;
  }

  const prompt = `
    You are an expert social media strategist and copywriter.
    
    TASK: Refine the following ${contentType === 'copy' ? 'social media post copy' : 'visual concept description'} based on the user's specific instruction.
    
    STRICT RULE: Do NOT overhaul the entire post unless the user explicitly asks for a "complete rewrite" or a "new concept". 
    Maintain the existing theme, context, and core message (e.g., if it's a "Workers' Day" post, it MUST remain a "Workers' Day" post).
    ONLY apply the requested changes or additions.
    
    CURRENT ${contentType.toUpperCase()}:
    ${currentText}
    
    USER INSTRUCTION:
    ${userPrompt}
    
    ${parentContextSection}
    
    BRAND CONTEXT:
    Brand Name: ${brand.companyName}
    ${profileContext}
    Target Audience: ${brand.questionnaire.targetAudience}
    Brand Voice: ${brand.questionnaire.brandVoice}

    AVAILABLE BRAND ASSETS (Use these if the user mentions them by name/type):
    - Visual Templates/Assets: ${brand.brandTemplates?.map(t => `${t.name}: ${t.description || 'N/A'}`).join(', ') || 'None'}
    - Products/Services: ${brand.productImages?.map(p => `${p.name}: ${p.description || 'N/A'} (Price: ${p.price || 'N/A'})`).join(', ') || 'None'}
    - Team Directory/Members: ${brand.teamDirectory?.map(t => `${t.name} (${t.role}): ${t.bio || 'N/A'}`).join(', ') || 'None'}
    
    ${docParts.length > 0 ? '\nI have attached context documents containing pricing and strategy. Use them to ensure facts (like prices) are accurate.' : ''}
    
    REFINEMENT RULES:
    1. If the user mentions a team member, character, mascot (like "Steve"), or product found in the ASSETS above, incorporate it explicitly into the content.
    2. If the user asks for a price change, use the pricing found in the BRAND CONTEXT or documents.
    3. If the instruction is vague (e.g., "more pop"), apply a subtle creative lift while keeping the same copy structure.
    4. Return ONLY the refined ${contentType}. No conversational filler.
  `;

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [
      ...docParts.map(p => ({ parts: [{ inlineData: p.inlineData }] })),
      { parts: [{ text: prompt }] }
    ]
  });

  return fixCaptionSpacing(response.text.trim());
}

export async function refineMonthlyPlan(
  brand: BrandProfile,
  calendar: ContentIdea[],
  userPrompt: string,
  targetChannels?: string[]
): Promise<ContentIdea[]> {
  const refinedCalendar: ContentIdea[] = [];
  
  // Process in batches of 10 to maintain context and quality while respecting token limits
  const batchSize = 10;
  for (let i = 0; i < calendar.length; i += batchSize) {
    const batch = calendar.slice(i, i + batchSize);
    
    const profileContext = brand.accountType === 'personal'
      ? `Profile Type: Personal Brand/Creator. Use first-person ("I", "me", "my").`
      : `Profile Type: Company/Business. Use standard brand voice ("we", "our").`;

    const platformsContext = targetChannels && targetChannels.length > 0
      ? `\n       CRITICAL: The user wants to change the target platforms for this plan to: ${targetChannels.join(', ')}. Adapt ALL post copies and concepts to be optimal for these specific platforms.`
      : '';

    const prompt = `
      As a Social Media Content Strategist, refine this batch of content ideas based on the user's instructions.
      
      USER INSTRUCTION: "${userPrompt}"
      
      BRAND CONTEXT:
      Brand: ${brand.companyName}
      ${profileContext}
      Voice: ${brand.questionnaire.brandVoice}${platformsContext}

      AVAILABLE BRAND ASSETS:
      - Products/Services: ${brand.productImages?.map(p => `${p.name}: ${p.description || 'N/A'} (Price: ${p.price || 'N/A'})`).join(', ') || 'None'}
      - Team Directory/Members: ${brand.teamDirectory?.map(t => `${t.name} (${t.role}): ${t.bio || 'N/A'}`).join(', ') || 'None'}
      - Visual Templates: ${brand.brandTemplates?.map(t => `${t.name}: ${t.description || 'N/A'}`).join(', ') || 'None'}
      
      BATCH TO REFINE:
      ${JSON.stringify(batch.map(item => ({
        id: item.id,
        date: item.date,
        copy: item.copy,
        concept: item.concept,
        platforms: targetChannels && targetChannels.length > 0 ? targetChannels : item.platforms,
        slides: item.slides.map(s => ({ id: s.id, concept: s.concept, overlayText: s.overlayText || "" }))
      })))}
      
      REFINEMENT RULES:
      1. Apply the user instruction to EVERY item in the batch.
      2. If the instruction implies translating (e.g., "Translate to Portuguese"), you must translate the post 'copy', 'concept', and each slide's 'concept' and 'overlayText' completely.
      3. Maintain the IDs and dates exactly.
      4. Maintain the core theme of each post (e.g. if it's about a specific event on a specific date, don't change that event).
      5. CAROUSEL POST QUALITY RULES (If post format is carousel): Every slide concept and overlay text must remain aligned as a swipe-worthy visual story. If a slide concept mentions or implies rendering text (checklists, summaries, headlines, quotes), you MUST specify the exact text in 'overlayText'.
      6. Return ONLY a JSON array of objects with refined fields (id, copy, concept, slides).
      
      Return ONLY valid JSON.
    `;

    try {
      const response = await callGeminiProxy({
        model: "gemini-2.5-flash",
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                id: { type: Type.STRING },
                copy: { type: Type.STRING },
                concept: { type: Type.STRING },
                slides: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      id: { type: Type.STRING },
                      concept: { type: Type.STRING, description: "Specific visual concept for this slide. MUST follow the narrative flow rules (Slide 1 must be a bold scroll-stopping Hook; Slide 2 must show Problem/Context; Middle slides must teach 1 specific Value idea with continuous backdrop; Example-slide shows 'Do This, Not That'/template; Summary slide shows a saveable checklist; Final slide shows a clear CTA). For diagrams or infographics, describe exact anatomical parts, arrows, and labels in meticulous detail." },
                      imagePrompt: { type: Type.STRING, description: "A pure visual prompt for the background image generator. STRICTLY NO text, NO plaques, NO bento grids, and NO ui mockups. ONLY describe the scenic photography, colors, or objects." },
                      overlayText: { type: Type.STRING, description: "The exact headline, checklist points, list content, or quote text to be overlaid on this slide. MUST NOT be empty or blank if the slide concept implies or contains text, headlines, lists, bullet points, summaries, or quotes." }
                    },
                    required: ["id", "concept"]
                  }
                }
              },
              required: ["id", "copy", "concept", "slides"]
            }
          }
        }
      });

      const results = JSON.parse(response.text || "[]");
      
      // Merge results back into the original items
      batch.forEach(originalItem => {
        const refined = results.find((r: any) => r.id === originalItem.id);
        if (refined) {
          refinedCalendar.push({
            ...originalItem,
            copy: fixCaptionSpacing(refined.copy || ""),
            concept: fixCaptionSpacing(refined.concept || ""),
            platforms: targetChannels && targetChannels.length > 0 ? targetChannels : originalItem.platforms,
            slides: originalItem.slides.map(s => {
              const refinedSlide = refined.slides?.find((rs: any) => rs.id === s.id);
              return refinedSlide ? { 
                ...s, 
                concept: fixCaptionSpacing(refinedSlide.concept || ""),
                overlayText: refinedSlide.overlayText !== undefined ? fixCaptionSpacing(refinedSlide.overlayText) : s.overlayText
              } : s;
            })
          });
        } else {
          refinedCalendar.push(originalItem);
        }
      });
    } catch (err: any) {
      console.error(`Batch ${i/batchSize} refinement failed:`, err);
      await logError(`Monthly Plan Batch ${i/batchSize} refinement failed`, { userPrompt, error: err.message }, err);
      // Throw the error so the caller can handle it, prevent saving, and avoid charging tokens
      throw err;
    }
    
    // Brief delay between batches
    if (i + batchSize < calendar.length) {
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  return refinedCalendar;
}

export async function reflowCarousel(
  brand: BrandProfile,
  currentCopy: string,
  remainingSlides: { id: string; concept: string }[]
): Promise<{ copy: string; slides: { id: string; concept: string }[] }> {
  const profileContext = brand.accountType === 'personal'
    ? `Profile Type: Personal Brand/Creator. Use first-person ("I", "me", "my").`
    : `Profile Type: Company/Business. Use standard brand voice ("we", "our").`;

  const prompt = `
    You are an expert social media strategist.
    
    TASK: A slide was removed from a carousel post. You need to "reflow" the remaining content to ensure the narrative and messaging still flow perfectly across the remaining slides.
    
    CURRENT POST COPY:
    ${currentCopy}
    
    REMAINING SLIDES:
    ${remainingSlides.map((s, i) => `Slide ${i + 1} (ID: ${s.id}): ${s.concept}`).join('\n')}
    
    BRAND CONTEXT:
    Brand Name: ${brand.companyName}
    ${profileContext}
    Brand Voice: ${brand.questionnaire.brandVoice}
    
    REQUIREMENTS:
    - Update the overall post copy if necessary to match the new slide count.
    - Update the visual concepts for each remaining slide to ensure a logical progression (Introduction -> Value/Details -> Call to Action).
    - Maintain the brand voice.
    - Return the result as a JSON object with "copy" and "slides" fields.
    - Each slide in the "slides" array MUST keep its original "id".
  `;

  const config: any = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        copy: { type: Type.STRING },
        slides: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              id: { type: Type.STRING },
              concept: { type: Type.STRING }
            },
            required: ["id", "concept"]
          }
        }
      },
      required: ["copy", "slides"]
    }
  };

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{ parts: [{ text: prompt }] }],
    config: config
  });

  const parsed = JSON.parse(response.text);
  if (parsed && typeof parsed === 'object') {
    parsed.copy = fixCaptionSpacing(parsed.copy || "");
    if (parsed.slides && Array.isArray(parsed.slides)) {
      parsed.slides = parsed.slides.map((s: any) => ({
        ...s,
        concept: fixCaptionSpacing(s.concept || "")
      }));
    }
  }
  return parsed;
}

export async function generateInsightsStrategy(metricsSummary: string, postData: any): Promise<string> {
  const prompt = `Analyze these social media metrics and recent comments/posts to suggest a high-level content strategy.
    
    METRICS SUMMARY:
    ${metricsSummary}
    
    RECENT POST DATA/COMMENTS:
    ${JSON.stringify(postData, null, 2)}
    
    Provide professional, actionable advice (3-4 points) in a bold, technical, and motivating tone. 
    Focus on what works and what needs focus. 
    Format as short paragraphs.`;

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{ parts: [{ text: prompt }] }]
  });
  return response.text || "Strategy generation yielded no results.";
}

export async function enhanceShowcaseDescription(brandName: string, title: string, currentDescription: string): Promise<string> {
  const prompt = `
    You are an elite SEO copywriter. I have some brief text about a social media post showcase item.
    Brand: ${brandName || 'Unknown'}
    Title: ${title}
    Current Description: ${currentDescription}
    
    Write a compelling, detailed case study/description for this post.
    Format it as proper Markdown with:
    - A catchy H1 heading (use "# [Heading]")
    - Subheadings (H2, H3) where appropriate
    - Bullet points for impact or strategy
    - Write at least 2 paragraphs of excellent copy that highlights social media strategy.
    Do NOT wrap in \`\`\`markdown or \`\`\` tags. Just return the markdown string directly.
  `;

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{ parts: [{ text: prompt }] }]
  });
  return response.text?.replace(/```markdown/g, '').replace(/```/g, '').trim() || "";
}

export async function generatePanoramaBackgroundConcept(
  postCopy: string,
  slideConcepts: string[],
  companyName?: string,
  visualAesthetic?: string
): Promise<string> {
  const numSlides = slideConcepts.length;
  const prompt = `
    You are an expert creative director and lead prompt engineer for AI image generators.
    
    TASK: Analyze the copy, the brand context, and the list of ${numSlides} slide concepts for a widescreen continuous multi-panel corporate/professional social media carousel post. 
    Synthesize one unified, cohesive, widescreen landscape panorama wallpaper background description that will serve as the background canvas for all these slides.
    
    OVERALL POST COPY:
    ${postCopy}
    
    SLIDE CONCEPTS (What will be placed on each portion of the landscape background panorama):
    ${slideConcepts.map((c, i) => `Slide ${i + 1}: ${c}`).join('\n')}
    
    BRAND NAME:
    ${companyName || 'Professional Brand'}

    VISUAL AESTHETIC / DIRECTION:
    ${visualAesthetic || 'Modern, clean, and professional.'}
    
    STRICT PROMPT RULES FOR PANORAMA CONCEPTS:
    1. Continuous Backdrop Assurance: You MUST guarantee a purely scenic, textless, single-image landscape backdrop that spans cleanly from far-left to far-right without vertical bars, layout panels, panel shadows, borders, vertical split lines, cards, blocks, or grid dividers. The backdrop is purely a structural layout background. Strictly forbid any vertical dividers, visible splits, frame boards, or container boxes that segment the image. It must look like one unified continuous photo or soft gradient canvas.
    2. Color Harmony & Flow: Identify the elegant core palette from the brand aesthetics and modern designs (e.g. muted sage green, warm brass accents, cream, charcoal). Define a cohesive blended background.
    3. Unbroken Wide-Angle Continuity: Describe ONE single, spacious, unified landscape scenery (e.g., an elegant wide-angle luxury wellness studio lounge or a fluid, seamless abstract watercolor backdrop). Do NOT segment your description into side-by-side "zones", "slides", "portions", or "segments". Do NOT describe repeating main subjects (for example, NEVER suggest "three dental chairs" or "multiple office desks"). There should be at most a single main subject or accent, balanced with deep cinematic breathing room (negative space) and simple atmospheric details across the entire horizontal sweep.
    4. Forbid Segmented Language: You are strictly forbidden from using terms like "segments", "zones", "sections", "left side", "middle portion", "right side", "three parts", "stages", or "divided panels" in your output. Describe the whole image as a single, coherent, fluid panoramic landscape.
    5. Zero Typography: The backdrop MUST NOT mention text, typography, quotes, speech bubbles, letters, or logos. It is strictly a visual room scenery, clean studio scene, office interior, soft atmosphere, or premium abstract background.
    6. Composition & Vertical Belt Guardrail: Introduce generous, clean negative space. 
       CRITICAL CENTERING RULE: Group and align all graphic details, scenery, and colors tightly along the central horizontal 40% height portion of the canvas (forming a central horizontal belt). This ensures that when the widescreen image is sliced into square panes vertically centered, the crucial design details are fully visible and do not get cropped off at the top or bottom.
    7. Return ONLY the raw visual prompt description. Do NOT include phrases like "Here is your prompt", "Sure", "Okay", or any markdown symbols like code-blocks \`\`\`. Just return the final 2-3 sentence prompt itself directly.
  `;

  const response = await callGeminiProxy({
    model: "gemini-2.5-flash",
    contents: [{ parts: [{ text: prompt }] }]
  });
  
  return response.text?.replace(/```[a-z]*/g, '').replace(/```/g, '').trim() || "Elegant, modern branded background design concept. Simple and professional.";
}

/**
 * Smart Lucide Icon Mapper
 * Suggests a validated PascalCase Lucide icon name based on text content.
 */
export async function suggestLucideIcon(text: string): Promise<string> {
  if (!text || text.trim() === "") {
    return "Check";
  }

  const prompt = `
    Given the following bullet point or item text, select the single most relevant and visually appropriate icon name from the Lucide React icon library.

    ITEM TEXT:
    "${text}"

    STRICT RULES:
    1. Reply with ONLY the exact PascalCase name of the icon (e.g., "TrendingUp", "ShieldCheck", "Heart", "Clock", "Award", "BookOpen", "Activity", "MessageSquare", "Scale", "Smile", "Wrench", "DollarSign", "Target", "Users", "Zap", "HelpCircle").
    2. Do NOT include any markdown, backticks (\`\`\`), explanation, introductory text, punctuation, or spaces.
    3. If there is no highly specific icon, default to "Check" or "CheckCircle2".
  `;

  try {
    const response = await callGeminiProxy({
      model: "gemini-2.5-flash",
      contents: [{ parts: [{ text: prompt }] }]
    });

    const iconName = response.text
      ?.replace(/```[a-z]*/g, "")
      .replace(/```/g, "")
      .replace(/['"\\s.]/g, "")
      .trim();

    return iconName || "Check";
  } catch (error) {
    console.error("[suggestLucideIcon] Failed to suggest icon:", error);
    return "Check";
  }
}


